import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spinner } from '@react-pdf-viewer/core';
import {
  Modal, Button, Icon, List, Checkbox, Label, Message, Loader,
  Segment, Dropdown, Pagination, Input, Menu, Progress, TextArea, Table,
  Divider
} from 'semantic-ui-react';
import {
  fetchLitGroupCast,
  fetchCharacterContext,
  mergeCharacters,
  unmergeCharacters,
  dismissCharacters,
  rejectMerge,
  unrejectMerge,
  clearCastFeedback,
  selectCastPartitioned,
  selectCastBooks,
  selectCastLoading,
  selectCastError,
  selectCastBlocked,
  selectCastLastResult,
  selectContextByChar,
} from '../../slices/litGroupCastSlice';
import { apiClient } from '../../apiTools/apiClient';
import { config } from '../../constants/config';

import ConfirmModal from './ConfirmModal';
// ⭐ Hear three takes for a character and choose one. Beside the list that generated them.
import VoiceCandidateReview from './VoiceCandidateReview';

/**
 * ⭐ ONE definition of how edits reach the server, used by the dry run AND the real run.
 *
 * ⛔ Two call sites building this by hand is how a reviewed prompt and a generated prompt drift
 * apart — which would make the review surface a liar about the one thing it exists to show.
 *
 * ⚠️ A sample under 100 characters is dropped rather than sent: Voice Design refuses shorter text,
 * and a 422 in the middle of a long run reads like something else entirely.
 */
const castEditPayload = (edits) => {
  const promptOverrides = {};
  const sampleOverrides = {};
  Object.entries(edits || {}).forEach(([uuid, e]) => {
    if (e && typeof e.prompt === 'string' && e.prompt.trim().length >= 20) {
      promptOverrides[uuid] = e.prompt.trim();
    }
    if (e && typeof e.sampleText === 'string' && e.sampleText.trim().length >= 100) {
      sampleOverrides[uuid] = e.sampleText.trim();
    }
  });
  const out = {};
  if (Object.keys(promptOverrides).length) out.promptOverrides = promptOverrides;
  if (Object.keys(sampleOverrides).length) out.sampleOverrides = sampleOverrides;
  return out;
};

/**
 * ManageCastModal — dedup an imported LitGroup's cast.
 *
 * A production PDF import commonly explodes one character into name-variants
 * (Diogenes QPack / QPack / QP / Pack) and coughs up spurious "characters"
 * (a newspaper title, a blurb author, "Unknown"). This modal folds variants
 * into a canonical (non-destructive: persists `aliases`; the importer and the
 * renderer resolve variant speaker strings to the canonical's voice) and
 * dismisses the spurious ones. Recorded / VA-assigned characters are guarded
 * server-side and surface here as locked.
 *
 * @param {boolean} open       modal mode: whether the modal is open
 * @param {boolean} inline     render the tool directly (no Modal chrome), always loaded
 * @param {string} litGroupUuid  the LIVE LitGroup uuid (activePrototype.publishedLitGroupUuid)
 * @param {string} litGroupName
 * @param {Function} onClose
 */
/**
 * The Book Filter's "all" value.
 *
 * A SENTINEL STRING rather than null, because Semantic reads a null/undefined Dropdown value as
 * "nothing selected" and renders the trigger EMPTY -- so the default showed no text at all and the
 * control looked broken until something was picked. A real value makes the dropdown display its own
 * selection, default included, which is what a filter has to do to be trusted.
 */
const ALL_BOOKS = '__all_books__';
const PAGE_SIZE = 25;

// Name filter: match on the character's name, its aliases, and any merged variant names.
const nameMatches = (c, q) => {
  if (!q) return true;
  const hay = [c.name, ...(c.aliases || []), ...((c.mergedVariants || []).map((v) => v.name))]
    .filter(Boolean).join(' ').toLowerCase();
  return hay.includes(q);
};

/**
 * Why a character cannot be auditioned, in words that imply the remedy.
 *
 * ⛔ The server returns a machine reason; showing it raw would make the operator guess. These are
 * deliberately asymmetric in tone: no-derivation-yet is a one-click fix, has-a-real-recording is a
 * permanent and correct refusal, and the difference matters more than the words do.
 */
const CAST_BLOCK_REASONS = {
  // ⛔ Their dialogue was reassigned or joined elsewhere, so there is nothing to voice.
  // Running a derivation cannot help — a derivation does not give a character its lines back.
  'no-lines-left': 'has no lines left — their dialogue was reassigned',
  'no-derivation-yet': 'not derived yet — run Update Casting first',
  'has-a-real-recording': 'has a real recording — a design must never replace human performance',
  'merged-or-dismissed': 'merged away or dismissed',
  inactive: 'inactive',
  'not-in-this-group': 'not in this Literary Group',
};
const ManageCastModal = ({
  open, inline = false, litGroupUuid, litGroupName, onClose,
  /**
   * ⭐ Fired when a voice is actually bought and attached, so a HOST holding its own list of
   * voice designs can refetch it.
   *
   * ⛔ WITHOUT THIS the cast table’s voice dropdown lied. TalentAgencyPage fetches
   * /voice-designs once, when a LitGroup is selected; casting happens in here, which refreshed
   * the cast but never the host’s design list. So a voice that HAD been assigned was missing
   * from the dropdown beside it — which reads exactly like a casting that did not take.
   */
  onVoiceCast,
}) => {
  const dispatch = useDispatch();
  const { real, suspect } = useSelector(selectCastPartitioned);
  const books = useSelector(selectCastBooks) || [];
  /**
   * BOOK FILTER. null means All books, which is the default: the cast list's job is the whole
   * group, and the filter is for narrowing. The operator switches to a book after an ingestion.
   */
  const [bookFilter, setBookFilter] = useState(ALL_BOOKS);
  // Read this rather than testing bookFilter for truthiness: ALL_BOOKS is a non-empty string.
  const bookFiltered = bookFilter !== ALL_BOOKS;
  /**
   * NEW AND UNVOICED ONLY — the post-ingestion work list: speaks in the selected book and no
   * other, and has no voice yet. Kept SEPARATE from the book picker so the two questions stay
   * separable; a compound filter would make "show me this book" impossible to ask on its own.
   */
  const [newUnvoicedOnly, setNewUnvoicedOnly] = useState(false);
  const loading = useSelector(selectCastLoading);
  const error = useSelector(selectCastError);
  const blocked = useSelector(selectCastBlocked);
  const lastResult = useSelector(selectCastLastResult);
  const contextByChar = useSelector(selectContextByChar);

  // uuids checked as "act on these" (merge targets OR dismiss targets)
  const [selected, setSelected] = useState([]);
  const [canonicalUuid, setCanonicalUuid] = useState(null);
  // uuids whose sample-line context panel is open
  const [expanded, setExpanded] = useState([]);
  // The open confirmation, if any: { kind, title, body, items, confirmLabel, danger?, character? }
  const [confirm, setConfirm] = useState(null);
  // name filter + per-section page (the cast can run to hundreds of entries)
  const [query, setQuery] = useState('');
  const [realPage, setRealPage] = useState(1);
  const [suspectPage, setSuspectPage] = useState(1);
  const [activeTab, setActiveTab] = useState('cast');   // 'cast' | 'nodialogue' | 'reassign' | 'reassigned' | 'auditions'
  const [mode, setMode] = useState('name');             // the search box: 'name' | 'text'
  const [hits, setHits] = useState(null);               // { total, lines } for a text search
  /**
   * ⭐ Bumped by anything that CHANGES a line, so the text-search results re-run.
   *
   * ⛔ The reassign handler refreshed the cast, the suggestions and the log — everything except the
   * list the operator was looking at. So a reassigned line sat there still showing its old speaker,
   * which reads exactly like a reassignment that did not take (and on 2026-10-03, while three real
   * reasons for that were being fixed, it was indistinguishable from them).
   *
   * ⚠️ A nonce rather than a local patch of the row, deliberately: a reassign can also carry to an
   * edit-chain part the operator did not name, so only the server knows the full result.
   */
  const [searchNonce, setSearchNonce] = useState(0);
  const [searching, setSearching] = useState(false);
  const [log, setLog] = useState([]);                   // every line a human has moved
  // Guided Merge — server-computed name-containment suggestions; client dismiss + canonical override.
  const [suggestions, setSuggestions] = useState([]);
  // Names one character apart that containment can NEVER propose — a typo, an OCR slip, a survived
  // line-break hyphen. Held separately from `suggestions` because it is a weaker claim, and because
  // the screen's own counter-example (Victoria / Viktoria — two different women) is inside the band.
  const [nearMisses, setNearMisses] = useState([]);
  // ⭐ THE LOCAL "not the same" LIST IS GONE (2026-08-15). It was `useState([])`, so every refusal
  // died with the tab and the operator re-answered the same suggestions on each load — reported
  // against QPack / Mister Diogenes QPack, which is the bridging compound the engine's own header
  // warns about by name. The refusal is now a NOT_MERGED_WITH edge, so the suggestion is never
  // regenerated rather than being hidden after the fact, and `rejectingSug` here is nothing but the
  // in-flight spinner for the click.
  const [rejectingSug, setRejectingSug] = useState(null);
  const [sugCanonical, setSugCanonical] = useState({});   // key → chosen canonical uuid (override)
  const [mergingSug, setMergingSug] = useState(null);     // key mid-merge
  // The line currently being reassigned: { lineId, bookUuid, text, was }. A reassign answers a
  // different question from a merge — "THIS line belongs to someone else" rather than "these two
  // labels are one person" — and it is the only correct mechanism for a label that denotes
  // different people in different places.
  const [reassign, setReassign] = useState(null);
  const [reassignTo, setReassignTo] = useState(null);
  // The dialog's own filter box. Separate from the page's `query` on purpose: that one is filtering
  // the cast list behind the dialog, and typing a name to reassign to must not disturb what you
  // were looking at.
  const [reassignFilter, setReassignFilter] = useState('');
  const [reassigning, setReassigning] = useState(false);
  // Names the merge engine cannot evaluate at all — returned by the server and, until now, dropped.
  const [unevaluated, setUnevaluated] = useState([]);
  /**
   * GROUPINGS THE ENGINE FOUND AND REFUSED. Held apart from both suggestions and nearMisses
   * because it is a third kind of claim: the engine assembled these and then declined to propose
   * them, because a string rule cannot tell a first-name/surname pair from two people named
   * together. Before this they were discarded in the response, so the only way to find them was
   * to read every name in the cast.
   */
  const [withheld, setWithheld] = useState([]);
  /**
   * PANEL EXPAND/COLLAPSE. The two actionable panels start OPEN and the unevaluated one stays
   * closed, which is how it already behaved: it is a read-only tail nobody acts on every visit,
   * while a suggestion hidden behind a click is a suggestion that does not get taken.
   */
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showWithheld, setShowWithheld] = useState(true);
  const [showNearMisses, setShowNearMisses] = useState(true);
  const [showUneval, setShowUneval] = useState(false);

  // The term a context panel should window around. Empty in name mode, so panels behave as before.
  const textQ = mode === 'text' ? query.trim() : '';

  const loadSuggestions = useCallback(async () => {
    if (!litGroupUuid) { setSuggestions([]); setNearMisses([]); return; }
    try {
      const r = await apiClient.get(`/litgroups/${litGroupUuid}/characters/merge-suggestions`);
      const withKey = (list) => (list || []).map((g) => ({ ...g, key: g.members.map((m) => m.uuid).sort().join(',') }));
      setSuggestions(withKey(r.data.suggestions));
      setNearMisses(withKey(r.data.nearMisses));
      setUnevaluated(r.data.unevaluated || []);
      // ⚠️ The server sends NO canonical for these on purpose. The radios need one to render, so
      // the most-spoken member is pre-selected as a starting point -- NOT as an endorsement, which
      // is why the section says so in words.
      setWithheld(withKey((r.data.withheld || []).map((w) => ({
        ...w, canonicalUuid: (w.members[0] || {}).uuid,
      }))));
    } catch { setSuggestions([]); setNearMisses([]); setUnevaluated([]); setWithheld([]); }
  }, [litGroupUuid]);

  // (Re)load the cast whenever the tool becomes active for a group (modal open, or inline mount).
  const active = open || inline;
  useEffect(() => {
    if (active && litGroupUuid) {
      setSelected([]);
      setCanonicalUuid(null);
      setExpanded([]);
      setQuery('');
      setRealPage(1);
      setSuspectPage(1);
      setActiveTab('cast');
      dispatch(clearCastFeedback());
      dispatch(fetchLitGroupCast(litGroupUuid));
    }
  }, [active, litGroupUuid, dispatch]);

  // Load Guided Merge suggestions when the tool activates for a group.
  useEffect(() => {
    // Nothing to reset for refusals any more — they live in the graph, so re-opening the tool on a
    // group shows exactly what was decided last time. Only the canonical picks are per-session.
    if (active && litGroupUuid) { setSugCanonical({}); loadSuggestions(); }
  }, [active, litGroupUuid, loadSuggestions]);

  /**
   * ⭐ RE-FETCH CONTEXT FOR PANELS THAT ARE STILL OPEN (2026-08-15, operator report: the
   * "loading context…" spinner stays up after actions).
   *
   * `fetchLitGroupCast.fulfilled` clears the whole context cache, and rightly — a merge makes every
   * cached passage stale. But nothing re-requested it, so any panel left open found no entry, and
   * `renderContext` renders a MISSING entry identically to a LOADING one. The result was a spinner
   * with no request behind it, sitting there until the row was collapsed by hand. Every action that
   * refreshes the cast hit it: merge, unmerge, dismiss, reject, reassign.
   *
   * Self-limiting rather than loop-prone: the pending reducer writes `{ loading: true }` immediately,
   * so the guard is false on the very next run.
   */
  useEffect(() => {
    if (!litGroupUuid) return;
    expanded.forEach((uuid) => {
      const entry = contextByChar[uuid];
      // Refetch when the panel is missing OR when the search term it was built for has changed —
      // otherwise a panel keeps showing the previous query's matches.
      if (!entry || (!entry.loading && (entry.q || '') !== textQ)) {
        dispatch(fetchCharacterContext({ litGroupUuid, characterUuid: uuid, q: textQ }));
      }
    });
  }, [expanded, contextByChar, litGroupUuid, dispatch, textQ]);

  // Any filter change returns both sections to page 1 (else the page can fall past the results).
  const q = query.trim().toLowerCase();
  useEffect(() => { setRealPage(1); setSuspectPage(1); }, [q]);

  const isProtected = (c) => c.hasRecording || c.hasAssignment;

  const toggle = useCallback((uuid) => {
    dispatch(clearCastFeedback());
    setSelected((prev) =>
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    );
  }, [dispatch]);

  const toggleExpand = useCallback((uuid) => {
    // ⭐ THE DISPATCH MUST HAPPEN OUTSIDE THE UPDATER (2026-08-10). This used to lazy-load the
    // character context from INSIDE setExpanded's updater function, which produced:
    //
    //   Warning: Cannot update a component (ManageCastModal) while rendering a different
    //   component (ManageCastModal).
    //
    // A state updater has to be pure. React calls it while processing the update — during the
    // render phase — so a Redux dispatch in there updates a component mid-render, which is what
    // React was objecting to. It was not a misplaced setState in the render body, which is where
    // that warning usually points and why it reads as though nothing is wrong here.
    //
    // Worse than the warning: StrictMode invokes updaters TWICE in development, so every expand
    // fired the context fetch twice — two requests, one of them redundant, for every character
    // opened.
    //
    // Deciding here instead is both correct and clearer: toggleExpand runs from a click, so
    // `expanded` is current, and the updater goes back to being a pure toggle.
    const willOpen = !expanded.includes(uuid);
    if (willOpen) {
      // Lazy-load this character's surrounding context on first open (cached thereafter).
      const cached = contextByChar[uuid];
      if (!cached || (!cached.loading && !cached.passages && !cached.error)) {
        dispatch(fetchCharacterContext({ litGroupUuid, characterUuid: uuid, q: textQ }));
      }
    }
    setExpanded((prev) => (
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    ));
  }, [dispatch, litGroupUuid, contextByChar, expanded, textQ]);

  /**
   * Changing tab closes the text-search results.
   *
   * The hits panel is rendered ABOVE the tabs — "a search is a lens onto the cast list, not a
   * different screen" — which is right while you are working one tab, and wrong the moment you leave
   * it: the results stay open over a list they no longer have anything to do with, and the box still
   * holds a search you have finished with. Clearing the query is all that is needed; the debounced
   * effect keys off it and drops the hits itself. The MODE is left alone, so a return to text search
   * does not need re-arming.
   */
  const switchTab = useCallback((tab) => {
    setActiveTab(tab);
    setQuery('');
  }, []);

  const allChars = useMemo(() => [...real, ...suspect], [real, suspect]);

  // Who the armed line can be given to: the whole cast, narrowed by the dialog's own filter box and
  // ordered by line count, so the people who actually speak in this book come first.
  const reassignCandidates = useMemo(() => {
    const f = reassignFilter.trim().toLowerCase();
    return allChars
      .filter((x) => x.name && (!f || x.name.toLowerCase().includes(f)))
      .sort((a, b) => (b.dialogueCount || 0) - (a.dialogueCount || 0) || a.name.localeCompare(b.name));
  }, [allChars, reassignFilter]);

  // Name-filter + paginate each section independently. Selection/canonical state lives outside the
  // rendered page, so you can check variants across pages and still merge them together.
  // ⚠️ THE NAME FILTER MUST NOT RUN IN TEXT MODE (2026-08-16). `q` was applied to the cast list
  // whatever the box was searching, so typing "volcano" to find a line emptied the cast beneath the
  // results — no character is NAMED volcano. The list and the search answer different questions and
  // only one of them is about names.

  /**
   * BOOK + NEW-AND-UNVOICED FILTERING, applied to the LIST only.
   *
   * The canonical dropdown below reads allChars and is deliberately NOT narrowed: you tick the new
   * fragments a fresh import produced, but the character you merge them INTO is usually an existing
   * one from another book. A Ghost name folding into Diogenes is the normal case.
   *
   * Same principle the name filter already documents -- the list and the merge target answer
   * different questions -- and selection state lives outside the rendered page, so a tick survives
   * changing the filter.
   */
  const linesInBook = useCallback(
    (c) => (c.bookLines || []).find((b) => b.bookId === bookFilter)?.lines || 0,
    [bookFilter]
  );
  const passesBook = useCallback((c) => {
    if (bookFilter === ALL_BOOKS) return true;
    const inThisBook = (c.bookLines || []).some((b) => b.bookId === bookFilter);
    if (!inThisBook) return false;
    if (!newUnvoicedOnly) return true;
    // NEW to the group: speaks here and nowhere else. UNVOICED: nothing recorded or assigned yet.
    const onlyThisBook = (c.bookLines || []).length === 1;
    return onlyThisBook && !c.hasRecording && !c.hasAssignment;
  }, [bookFilter, newUnvoicedOnly]);
  const nameQ = mode === 'name' ? q : '';
  const filteredReal = useMemo(
    () => real.filter((c) => nameMatches(c, nameQ) && passesBook(c)),
    [real, nameQ, passesBook]
  );
  const filteredSuspect = useMemo(
    () => suspect.filter((c) => nameMatches(c, nameQ) && passesBook(c)),
    [suspect, nameQ, passesBook]
  );

  /**
   * ─── ⭐ CAST VOICES FOR WHAT THE FILTER IS SHOWING ──────────────────────────────────────────────
   *
   * ⛔ THE SELECTION MODEL IS THE FILTER, not a second set of ticks. The Book Filter plus "new &
   * unvoiced only" already answers the question an ingestion raises — *which characters are new to
   * this group and have no voice* — and asking the operator to re-tick that same set in a different
   * panel is how a 73-character pass gets abandoned half-done. So the button acts on the list in
   * front of them and says how many that is.
   *
   * ⚠️ TWO PHASES, AND ONLY THE SECOND COSTS OPERATIONS. Generating three auditions per character
   * spends none; committing one spends 2 of a 1,040/month ration that cannot be bought. The preflight
   * below is therefore about ELIGIBILITY rather than money — the money question comes later, one
   * character at a time, after listening.
   */
  const [castPreflight, setCastPreflight] = useState(null);   // the server's eligibility verdict
  const [castBusy, setCastBusy] = useState(false);

  /**
   * ⛔ A CHARACTER WHO ALREADY HAS A VOICE IS NOT AUDITIONED UNLESS THIS IS TICKED.
   *
   * The first cut sent every ELIGIBLE uuid, and `would-replace-a-designed-voice` is eligible — so a
   * book filter without "new & unvoiced only" auditioned the whole cast, and the operator opened the
   * queue to find characters they had already cast by ear. The preflight counted them and said so,
   * which is not the same as letting them out.
   *
   * ⚠️ Replacement is a different decision from a first casting: it throws away a voice somebody
   * chose, and casting the replacement costs 2 operations. So it defaults OFF and is a deliberate
   * tick, not a thing you get by forgetting a filter.
   */
  const [castIncludeReplacing, setCastIncludeReplacing] = useState(false);

  /**
   * ─── ⭐ READ THE PROMPTS BEFORE MAKING ANY AUDIO ────────────────────────────────────────────────
   *
   * ⛔ The prompt is what decides whether an audition is worth hearing, and it was invisible until
   * three takes per character already existed. `docs/journal/MASK_VOICE_PROMPTS.md` measures the
   * cost of that order: auto-cast described Landrow Marble as "deep and resonant… deliberate
   * pacing", which is nearly the prompt of the woman he talks to for three chapters, and nothing on
   * screen could have shown it before the audio was made.
   *
   * ⚠️ It costs an LLM call and no voice operations, and writes nothing — so it is offered beside
   * the run rather than in front of it. The operator decides which they want.
   */
  const [castDryRun, setCastDryRun] = useState(null);   // { proposals, skipped } | { error }
  const [castDryBusy, setCastDryBusy] = useState(false);
  /**
   * ⭐ WHICH BATCH, OF HOW MANY. A multi-minute wait with a spinner and no count is
   * indistinguishable from a hang, and this one is long enough that the operator will reasonably
   * wonder. { done, total, batch, of, stopped? }
   */
  const [castDryProgress, setCastDryProgress] = useState(null);
  /**
   * ⭐ WHICH REFUSED BRIEF IS OPEN. The remedy for a content refusal is to rewrite the brief, and
   * the operator cannot rewrite text they have not seen — "it was blocked" without the words is
   * an instruction to guess. Collapsed by default because these are 900 characters each.
   */
  const [refusedOpen, setRefusedOpen] = useState(null);

  /**
   * ─── ⭐ THE OPERATOR'S PROMPT SUPERSEDES THE COMPOSED ONE ───────────────────────────────────────
   *
   * ⛔ WHY EDITING IS THE POINT, not a courtesy. The composer is measurably short of a good brief:
   * the hand-written prompts in docs/journal/MASK_VOICE_PROMPTS.md run 800-900 characters against a
   * composer capped at 300, and one of them exists BECAUSE the composed version was wrong — Landrow
   * Marble came back as "deep and resonant... deliberate pacing", which is nearly the prompt of the
   * woman he spends three chapters talking to.
   *
   * ⚠️ So an edited prompt is not an override of a correct answer. It is the answer, and it is what
   * the run sends. Recreate throws it away and asks the composer again — a fresh roll, since
   * composition runs warm, not a promise of different text.
   *
   * ⭐ HELD HERE rather than inside the dialog, so closing the editor does not lose the writing.
   */
  const [castEdits, setCastEdits] = useState({});       // uuid -> { prompt?, sampleText? }
  const [castEditing, setCastEditing] = useState(null); // the open editor

  /**
   * ─── ⭐ THE RUN IS INVISIBLE WITHOUT THIS ───────────────────────────────────────────────────────
   *
   * ⛔ Generation is a 202 and a background job: the POST returns instantly, three design calls per
   * character take minutes, and nothing on screen changed. The operator's only cue was an empty
   * Auditions tab telling them to refresh — indistinguishable from a run that never started.
   *
   * ⚠️ HELD HERE, NOT IN THE AUDITIONS TAB, because a tab body unmounts when you leave it and the
   * run does not. The strip is rendered above the tabs so it is visible wherever the operator goes,
   * and the Auditions tab reads it as a prop.
   *
   * ⭐ `castNonce` is bumped on completion so the queue refetches itself — the one moment when
   * there is definitely something new to see.
   */
  const [castRun, setCastRun] = useState(null);
  const [castNonce, setCastNonce] = useState(0);
  const castEsRef = useRef(null);
  const closeCastEs = useCallback(() => {
    if (castEsRef.current) { try { castEsRef.current.close(); } catch (_) { } castEsRef.current = null; }
  }, []);
  useEffect(() => () => closeCastEs(), [closeCastEs]);

  // The SSE stream is keyed by userId, the same way the re-ingest button and TalentAgency do it.
  const userState = useSelector((st) => st.user);
  const userData = userState?.data || userState?.profile?.data || userState || {};
  const userId = userData.userId || userData.id;

  /**
   * Watch one generation run. Subscribed BEFORE the POST fires, so a short run's COMPLETE is not
   * missed in the gap — the same ordering CoverageReport settled on.
   */
  const watchCastRun = useCallback((expected) => {
    closeCastEs();
    setCastRun({ active: true, percent: 3, message: 'Starting…', expected: expected || null });
    if (!userId) {
      // ⚠️ Honest about the blindness rather than silently showing a bar that will never move.
      setCastRun({
        active: true, percent: null, expected: expected || null,
        message: 'Auditioning in the background — no live progress on this session; use Refresh.',
      });
      return;
    }
    let es;
    try {
      es = new EventSource(`${config.BASE_URL}/sse-endpoint?userId=${userId}`);
    } catch (_) {
      setCastRun({ active: true, percent: null, message: 'Auditioning in the background…', expected: expected || null });
      return;
    }
    castEsRef.current = es;
    es.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        const d = parsed.data || parsed;
        if (d.litGroupUuid && d.litGroupUuid !== litGroupUuid) return;
        if (d.type === 'CASTING_PROGRESS') {
          setCastRun((r) => ({
            ...(r || {}), active: true,
            percent: typeof d.percent === 'number' ? d.percent : (r && r.percent) || null,
            message: d.message || d.step || 'Auditioning…',
          }));
        } else if (d.type === 'CASTING_COMPLETE') {
          closeCastEs();
          // ⭐ The result in the operator's terms: how many characters can now be listened to, and
          // the reminder that none of it has been bought yet.
          const n = d.characters;
          setCastRun({
            active: false, done: true, percent: 100,
            message: d.designCandidates
              ? `${d.candidates || 0} audition${d.candidates === 1 ? '' : 's'} ready for `
              + `${n || 0} character${n === 1 ? '' : 's'} — nothing bought yet.`
              + (d.stockSamples ? ` ${d.stockSamples} had no line long enough and read stock text.` : '')
              : 'Casting run finished.',
            capMessage: d.capMessage || null,
            /**
             * ⛔ WHO WAS REFUSED, BY NAME. ElevenLabs can block an individual brief — "this prompt
             * potentially doesn't follow our safety guidelines" — and the run used to stop on it and
             * report no name, which leaves the operator with a failed pass and nowhere to look.
             *
             * ⭐ Kept in state rather than announced and lost, because the remedy is a piece of work:
             * open the preflight, read the prompts, rewrite the refused one, audition that character
             * again.
             */
            refused: ((d.skipped || []).filter((x) => x.reason === 'brief-refused-by-provider')),
            skipped: d.skipped || [],
          });
          setCastNonce((x) => x + 1);
          dispatch(fetchLitGroupCast(litGroupUuid));
        } else if (d.type === 'CASTING_ERROR') {
          closeCastEs();
          setCastRun({ active: false, done: true, error: d.message || 'The audition run failed' });
        }
      } catch (_) { /* keepalives are not JSON */ }
    };
    es.onerror = () => {
      // A dropped stream is not a failed run: the job is server-side and finishes regardless.
      setCastRun((r) => (r && r.active
        ? { ...r, percent: null, message: 'Still auditioning — the live feed dropped; use Refresh in Auditions.' }
        : r));
      closeCastEs();
    };
  }, [closeCastEs, userId, litGroupUuid, dispatch]);

  /** The characters the filter is currently showing — real ones only; suspects have no lines to cast. */
  const castTargets = useMemo(() => filteredReal.map((c) => c.uuid).filter(Boolean), [filteredReal]);

  /**
   * ⭐ ASK BEFORE SPENDING TIME, because the answer is often "fewer than you think".
   *
   * `selectCastTargets` drops a character with no `suggestedArchetype` and says nothing, so without
   * this an operator ticks forty and gets twelve. The server names the reason per character; the
   * dialog repeats it rather than summarising, because the remedy differs: `no-derivation-yet` means
   * run Update Casting, while `has-a-real-recording` means leave that character alone forever.
   */
  const openCastVoices = useCallback(async () => {
    if (!litGroupUuid || !castTargets.length) return;
    setCastBusy(true);
    try {
      const r = await apiClient.post(
        `/litgroups/${litGroupUuid}/casting/design-candidates/preview`, { targetUuids: castTargets },
      );
      setCastPreflight(r.data || null);
    } catch (err) {
      setCastPreflight({
        error: err?.response?.data?.error || err.message || 'Could not check these characters',
      });
    } finally {
      setCastBusy(false);
    }
  }, [litGroupUuid, castTargets]);

  /**
   * ─── ⭐ THE DRY RUN, ONE BATCH PER REQUEST ──────────────────────────────────────────────────────
   *
   * ⛔ WHY IT IS A LOOP. Composing 61 briefs is ~16 LLM calls of 4,000 tokens — minutes — against a
   * 30-second request timeout. Asking for all of them in one request died at 30s with nothing to
   * show, which is the worst outcome available: the work was done and thrown away.
   *
   * ⛔ AND WHY THE SERVER PICKS THE BATCHES. "Four per request" is not equivalent to "four per
   * call": the distinctness rule operates inside one composing call, and `batchByCoPresence` exists
   * because WHICH four share that call is what makes it work. A client slicing by its own order
   * would separate scene partners and silently undo it. So `/plan` returns the groups and this
   * walks them.
   *
   * ⭐ EACH ANSWER IS RENDERED AS IT ARRIVES, which turns the wait into reading time — and a run
   * that fails at batch twelve still leaves eleven batches of briefs to judge.
   */
  const castDryAbort = useRef(false);
  const runCastDryRun = useCallback(async (ids, editsOverride, { merge } = {}) => {
    if (!litGroupUuid || !ids || !ids.length) return;
    const edits = editsOverride || castEdits;
    castDryAbort.current = false;
    setCastDryBusy(true);
    if (!merge) setCastDryRun(null);
    setCastDryProgress(null);

    try {
      // ⚠️ A single character (an edit or a Recreate) needs no plan — it is one call by definition.
      let batches = [ids];
      if (ids.length > 1) {
        const planRes = await apiClient.post(
          `/litgroups/${litGroupUuid}/casting/design-candidates/plan`, { targetUuids: ids },
        );
        const planned = (planRes.data && planRes.data.batches) || [];
        if (planned.length) batches = planned.map((b) => b.map((x) => x.uuid));
      }

      /**
       * ⭐ THE LEDGER TRAVELS. Each request is handed the briefs composed by the ones before it, so
       * a character in batch twelve still knows what their scene partner from batch two sounds
       * like. Without this the ledger would reset per request and distinctness would stop at the
       * chunk boundary — the same bug as "contrasts only within its batch".
       */
      const ledger = {};
      const collected = [];
      const skipped = [];
      let composed = 0;

      for (let bi = 0; bi < batches.length; bi += 1) {
        if (castDryAbort.current) break;
        const batch = batches[bi];
        setCastDryProgress({ done: composed, total: ids.length, batch: bi + 1, of: batches.length });

        const r = await apiClient.post(
          `/litgroups/${litGroupUuid}/casting/design-candidates/dry-run`,
          { targetUuids: batch, written: ledger, ...castEditPayload(edits) },
          {},
          // ⚠️ Per-call, not global: one composing call is 10–25s, which does not fit the 30s
          // default with any margin, and raising the default would hide real hangs everywhere else.
          { timeout: 120000 },
        );
        const got = (r.data && r.data.proposals) || [];
        got.forEach((pr) => {
          collected.push(pr);
          if (pr.prompt) ledger[pr.uuid] = { name: pr.name, prompt: pr.prompt };
        });
        ((r.data && r.data.skipped) || []).forEach((x) => skipped.push(x));
        composed += got.length;

        // Rendered now rather than at the end — the wait becomes reading time.
        if (merge) {
          const fresh = new Map(got.map((x) => [x.uuid, x]));
          setCastDryRun((prev) => (prev && prev.proposals
            ? { ...prev, proposals: prev.proposals.map((x) => fresh.get(x.uuid) || x) }
            : { proposals: [...collected], skipped: [...skipped] }));
        } else {
          setCastDryRun({ proposals: [...collected], skipped: [...skipped] });
        }
      }

      setCastDryProgress(
        castDryAbort.current
          ? { done: composed, total: ids.length, stopped: true }
          : null,
      );
    } catch (err) {
      // ⚠️ Whatever arrived before the failure is KEPT — see the comment above. The error is
      // reported beside the briefs rather than instead of them.
      const message = err?.response?.data?.error || err.message || 'Could not compose the prompts';
      setCastDryRun((prev) => (prev && prev.proposals?.length
        ? { ...prev, error: message }
        : { error: message }));
      setCastDryProgress(null);
    } finally {
      setCastDryBusy(false);
    }
  }, [litGroupUuid, castEdits]);

  /**
   * ⭐ WHAT WILL ACTUALLY RUN — eligible minus the already-voiced, unless those were ticked in.
   *
   * ⛔ The dialog used to promise `eligible` and then send a different set, which is exactly how a
   * run that auditioned the whole cast looked correct on its way out.
   */
  /** ⛔ ONE definition of the set, so the dialog, the dry run and the run cannot disagree. */
  const castRunIds = useMemo(() => {
    if (!castPreflight || castPreflight.error) return [];
    const blockedIds = new Set((castPreflight.blocked || []).map((b) => b.uuid));
    const replacingIds = new Set((castPreflight.replacingList || []).map((x) => x.uuid));
    return castTargets.filter(
      (u) => !blockedIds.has(u) && (castIncludeReplacing || !replacingIds.has(u)),
    );
  }, [castPreflight, castTargets, castIncludeReplacing]);

  const castWillRun = useMemo(() => {
    if (!castPreflight || castPreflight.error) return 0;
    const n = castPreflight.eligible || 0;
    return castIncludeReplacing ? n : Math.max(0, n - (castPreflight.replacing || 0));
  }, [castPreflight, castIncludeReplacing]);

  /**
   * Start the run. Background + SSE on the server, so this returns immediately and the operator
   * watches the existing casting progress.
   *
   * ⚠️ Only the ELIGIBLE uuids are sent. Sending the blocked ones too would have the server drop
   * them silently a second time, which is the behaviour this whole preflight exists to replace.
   */
  const confirmCastVoices = useCallback(async () => {
    if (!litGroupUuid || !castPreflight || castPreflight.error) return;
    const ids = castRunIds;
    if (!ids.length) { setCastPreflight(null); return; }
    setCastBusy(true);
    try {
      watchCastRun(ids.length);
      await apiClient.post(
        `/litgroups/${litGroupUuid}/casting/design-candidates`,
        { targetUuids: ids, ...castEditPayload(castEdits) },
      );
      setCastPreflight(null);
      // ⭐ Land where the takes will appear, so the progress and the result share one screen.
      switchTab('auditions');
    } catch (err) {
      closeCastEs();
      setCastRun(null);
      setCastPreflight({
        ...castPreflight,
        error: err?.response?.data?.error || err.message || 'Could not start the run',
      });
    } finally {
      setCastBusy(false);
    }
  }, [litGroupUuid, castPreflight, castRunIds, castEdits, watchCastRun, closeCastEs, switchTab]);
  const realPages = Math.max(1, Math.ceil(filteredReal.length / PAGE_SIZE));
  const suspectPages = Math.max(1, Math.ceil(filteredSuspect.length / PAGE_SIZE));
  const safeRealPage = Math.min(realPage, realPages);
  const safeSuspectPage = Math.min(suspectPage, suspectPages);
  const realPageItems = filteredReal.slice((safeRealPage - 1) * PAGE_SIZE, safeRealPage * PAGE_SIZE);
  const suspectPageItems = filteredSuspect.slice((safeSuspectPage - 1) * PAGE_SIZE, safeSuspectPage * PAGE_SIZE);

  // Canonical options = every character NOT currently checked (a canonical can't
  // also be a merge target). Ordered by dialogue so the "real" one floats up.
  const canonicalOptions = useMemo(
    () =>
      allChars
        .filter((c) => !selected.includes(c.uuid))
        .map((c) => ({
          key: c.uuid,
          value: c.uuid,
          text: c.name,
          description: `${c.dialogueCount} line${c.dialogueCount === 1 ? '' : 's'}`,
        }))
        .sort((a, b) =>
          String(a.text || '').toLowerCase().localeCompare(String(b.text || '').toLowerCase())
        ),
    [allChars, selected]
  );

  // Keep the canonical valid. The dropdown DISPLAYS alphabetically, but default to the
  // highest-dialogue unchecked character (most likely the real one to merge into).
  useEffect(() => {
    if (canonicalUuid && selected.includes(canonicalUuid)) {
      setCanonicalUuid(null);
    }
    if (!canonicalUuid && canonicalOptions.length) {
      const candidates = allChars.filter((c) => !selected.includes(c.uuid));
      const best = candidates.reduce(
        (m, c) => (c.dialogueCount > (m ? m.dialogueCount : -1) ? c : m),
        null
      );
      setCanonicalUuid((best || candidates[0]).uuid);
    }
  }, [selected, canonicalOptions, canonicalUuid, allChars]);

  const busy = loading.merge || loading.dismiss || loading.unmerge;
  const canMerge = selected.length >= 1 && !!canonicalUuid && !busy;
  const canDismiss = selected.length >= 1 && !busy;

  // ⭐ RELOAD THE SUGGESTIONS TOO (2026-08-10). This refetched only the CAST, while the Guided
  // Merge list was loaded once when the tool activated and never again. Merging, dismissing and
  // unmerging all change which suggestions exist — most visibly on an unmerge, where the split-out
  // variants should reappear as a proposal and instead did not show until the Green Room's Curate
  // table was redrawn by hand, which remounts the component and re-runs the activation effect.
  const afterAction = useCallback(() => {
    setSelected([]);
    setCanonicalUuid(null);
    dispatch(fetchLitGroupCast(litGroupUuid));
    loadSuggestions();
  }, [dispatch, litGroupUuid, loadSuggestions]);

  // Guided Merge: merge one suggestion group into its (possibly operator-overridden) canonical, then
  // refresh the cast + recompute suggestions. Reuses the same guarded merge thunk as the manual path.
  const mergeSuggestion = useCallback(async (g, canon) => {
    const mergeUuids = g.members.map((m) => m.uuid).filter((u) => u !== canon);
    if (!mergeUuids.length) return;
    setMergingSug(g.key);
    try {
      await dispatch(mergeCharacters({ litGroupUuid, canonicalUuid: canon, mergeUuids })).unwrap();
      dispatch(fetchLitGroupCast(litGroupUuid));
      loadSuggestions();
    } catch { /* error / blocked already in store */ }
    finally { setMergingSug(null); }
  }, [dispatch, litGroupUuid, loadSuggestions]);

  /**
   * ✕ on ONE member — "this one is not the same as the rest of this group".
   *
   * ⭐ FANS OUT ACROSS EVERY OTHER MEMBER PRESENT, not just the canonical. Union-find is transitive
   * and a pair veto is not, so refusing only against the canonical would let the member rejoin
   * through a third name that legitimately contains both. Sending the whole set is what the
   * sentence means.
   *
   * Writes immediately, like Merge does, because the complaint this fixes is precisely that an
   * answered question came back. Undo lives on the cast row below, where the merged-variants
   * affordance already is.
   */
  const rejectMember = useCallback(async (g, member) => {
    const against = g.members.map((m) => m.uuid).filter((u) => u !== member.uuid);
    if (!against.length) return;
    setRejectingSug(`${g.key}:${member.uuid}`);
    try {
      await dispatch(rejectMerge({ litGroupUuid, uuid: member.uuid, againstUuids: against })).unwrap();
      dispatch(fetchLitGroupCast(litGroupUuid));
      loadSuggestions();
    } catch { /* error already in store */ }
    finally { setRejectingSug(null); }
  }, [dispatch, litGroupUuid, loadSuggestions]);

  /** "Not the same" on the whole group — ✕ every non-canonical member at once. */
  const rejectWholeGroup = useCallback(async (g, canon) => {
    const others = g.members.filter((m) => m.uuid !== canon).map((m) => m.uuid);
    if (!others.length) return;
    setRejectingSug(g.key);
    try {
      await dispatch(rejectMerge({ litGroupUuid, uuid: canon, againstUuids: others })).unwrap();
      dispatch(fetchLitGroupCast(litGroupUuid));
      loadSuggestions();
    } catch { /* error already in store */ }
    finally { setRejectingSug(null); }
  }, [dispatch, litGroupUuid, loadSuggestions]);

  /** Undo one recorded refusal, from the cast row. */
  const undoRejection = useCallback(async (c, other) => {
    try {
      await dispatch(unrejectMerge({ litGroupUuid, uuid: c.uuid, againstUuids: [other.uuid] })).unwrap();
      dispatch(fetchLitGroupCast(litGroupUuid));
      loadSuggestions();
    } catch { /* error already in store */ }
  }, [dispatch, litGroupUuid, loadSuggestions]);

  /**
   * ⛔ MERGE WAS THE ONE ACTION WITH NO CONFIRMATION (fixed 2026-09-18). Dismiss and Unmerge both
   * gated; Merge — the irreversible-feeling one that rewrites who speaks what — dispatched straight
   * from the button. That is how "voice" (1 line, ch49, the Nevergate) was folded into the ch33
   * serving machine without anything saying so: no dialog, and afterwards the row rendered the
   * members comma-joined, so a name containing a comma hid the extra member.
   *
   * ⭐ AND THE FACTS ARE ON IT, not just the names. A merge across distant chapters is the tell — a
   * label that speaks in ch49 is not a variant of a machine that speaks in ch33 — so each member
   * carries its line count and the chapters it speaks in, and the canonical says it keeps its name.
   * The information was always in the graph; it simply never reached the moment of decision.
   */
  const mergeItems = useCallback(() => {
    const where = (c) => {
      const chs = c.chapters || [];
      if (!chs.length) return 'no lines';
      const lines = `${c.dialogueCount} line${c.dialogueCount === 1 ? '' : 's'}`;
      return `${lines} · ch ${chs.join(', ')}`;
    };
    // ⚠️ THE CANONICAL IS NOT IN `selected`. The ticks choose what to FOLD; the canonical is picked
    // separately, from the characters that are NOT ticked (see `candidates` above). Reading it out of
    // `selected` found nothing — so the dialog said "Fold 0 names" and omitted the line naming the
    // character that keeps its name, on the very merge it was built to make legible.
    const rest = allChars.filter((c) => selected.includes(c.uuid));
    const canon = allChars.find((c) => c.uuid === canonicalUuid);
    return [
      ...(canon ? [`“${canon.name}” — ${where(canon)}  ← keeps this name`] : []),
      ...rest.map((c) => `“${c.name}” — ${where(c)}`),
    ];
  }, [allChars, selected, canonicalUuid]);

  const handleMerge = useCallback(() => {
    const canon = allChars.find((c) => c.uuid === canonicalUuid);
    setConfirm({
      kind: 'merge',
      title: `Fold ${selected.length} name${selected.length === 1 ? '' : 's'} into “${canon?.name || '—'}”?`,
      body: 'Every line attributed to the folded names will be spoken by this character’s voice. '
        + 'Check the chapters below: a name that speaks somewhere else is probably somebody else.',
      items: mergeItems(),
      confirmLabel: 'Merge',
      danger: false,
    });
  }, [allChars, canonicalUuid, selected, mergeItems]);

  const runMerge = useCallback(async () => {
    setConfirm(null);
    try {
      await dispatch(
        mergeCharacters({ litGroupUuid, canonicalUuid, mergeUuids: selected })
      ).unwrap();
      afterAction();
    } catch {
      /* error / blocked already in store */
    }
  }, [dispatch, litGroupUuid, canonicalUuid, selected, afterAction]);

  // Both of these name the characters they are about to affect, which is exactly what a native
  // confirm cannot show — a cast of thirty arrives as one comma-run. The list is the point.
  const handleDismiss = useCallback(() => {
    setConfirm({
      kind: 'dismiss',
      title: `Remove ${selected.length} entr${selected.length === 1 ? 'y' : 'ies'} from the cast?`,
      body: 'Dialogue lines are kept; only the (unrecorded) character profiles are removed.',
      items: allChars.filter((c) => selected.includes(c.uuid)).map((c) => c.name).filter(Boolean),
      confirmLabel: 'Remove from cast',
    });
  }, [selected, allChars]);

  const runDismiss = useCallback(async () => {
    setConfirm(null);
    try {
      await dispatch(dismissCharacters({ litGroupUuid, uuids: selected })).unwrap();
      afterAction();
    } catch {
      /* error / blocked already in store */
    }
  }, [dispatch, litGroupUuid, selected, afterAction]);

  const handleUnmerge = useCallback((c) => {
    setConfirm({
      kind: 'unmerge',
      character: c,
      title: `Split ${c.mergedVariants.length} variant${c.mergedVariants.length === 1 ? '' : 's'} back out of “${c.name}”?`,
      body: 'Each becomes its own character again, with its samples and opportunity restored.',
      items: (c.mergedVariants || []).map((v) => v.name).filter(Boolean),
      confirmLabel: 'Split them out',
      danger: false,
    });
  }, []);

  const runUnmerge = useCallback(async (c) => {
    setConfirm(null);
    const targetUuids = (c?.mergedVariants || []).map((v) => v.uuid);
    try {
      await dispatch(unmergeCharacters({ litGroupUuid, targetUuids })).unwrap();
      // The hand-maintained un-hide that used to live here is gone (2026-08-15). It existed only
      // because merging hid the suggestion in LOCAL state, so an unmerge had to remember to put it
      // back. Nothing is hidden locally now — the suggestion list is recomputed from the graph, and
      // an unmerge simply makes the members eligible again. A refusal is a separate, explicit act.
      afterAction();
    } catch {
      /* error already in store */
    }
  }, [dispatch, litGroupUuid, afterAction]);

  const canonicalName = useMemo(
    () => allChars.find((c) => c.uuid === canonicalUuid)?.name,
    [allChars, canonicalUuid]
  );

  // Aliases are stored as normalized nameKeys (lowercased). Title-case for display.
  // (Original-case variant names aren't retained today; they'll ride the IS_MERGED_INTO
  // edge once the reversible-merge model lands — see the tombstone design spec.)
  const prettyAlias = (s) => String(s || '').replace(/\b\w/g, (m) => m.toUpperCase());

  // Is this windowed line spoken by narration? (speaker 'Narrator' or type 'narration')
  const isNarration = (l) =>
    l.type === 'narration' || (l.speaker || '').trim().toLowerCase() === 'narrator';

  /** Text search, debounced — the box is typed into, not submitted. */
  useEffect(() => {
    if (mode !== 'text' || !litGroupUuid) { setHits(null); return undefined; }
    const term = query.trim();
    if (term.length < 2) { setHits(null); return undefined; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        // ⚠️ THE THIRD ARGUMENT IS THE QUERY STRING (2026-08-16). apiClient is NOT axios:
        // get(url, pathParams, queryParams, config). Passing `{ params: … }` second — the axios
        // shape — puts it in the path-substitution slot, where it is silently discarded, and the
        // request goes out with no query string at all. It looked correct and did nothing.
        const r = await apiClient.get(`/litgroups/${litGroupUuid}/lines/search`, {}, { q: term });
        if (!cancelled) setHits({ total: r.data.total || 0, lines: r.data.lines || [] });
      } catch { if (!cancelled) setHits({ total: 0, lines: [] }); }
      finally { if (!cancelled) setSearching(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [mode, query, litGroupUuid, searchNonce]);

  /** The log of every line a human has moved — this group's audit trail of judgement calls. */
  const loadLog = useCallback(async () => {
    if (!litGroupUuid) { setLog([]); return; }
    try {
      const r = await apiClient.get(`/litgroups/${litGroupUuid}/lines/reassigned`);
      setLog(r.data.lines || []);
    } catch { setLog([]); }
  }, [litGroupUuid]);

  useEffect(() => { if (active && litGroupUuid) loadLog(); }, [active, litGroupUuid, loadLog]);

  /**
   * Bind one line to a different character. Not a merge: nothing about the two LABELS changes, only
   * which character this line's speaker resolves to, so no alias is created and no future line
   * inherits the decision.
   */
  const doReassign = useCallback(async (target) => {
    if (!reassign || !target) return;
    const c = allChars.find((x) => x.uuid === target);
    if (!c) return;
    setReassigning(true);
    try {
      await apiClient.post(`/voice-rendering/${reassign.bookUuid}/reassign-speaker`, {
        lineIds: [reassign.lineId], speakerName: c.name, characterUuid: c.uuid,
      });
      setReassign(null); setReassignTo(null); setReassignFilter('');
      // The line has moved, so the roster counts are stale. Refreshing the cast also clears the
      // context cache, and the effect above re-requests it for any panel still open — so this does
      // not re-fetch context by hand, which would race that clear and fetch twice.
      dispatch(fetchLitGroupCast(litGroupUuid));
      loadSuggestions();
      loadLog();
      // The row the operator is looking at has moved too — re-run the search that found it.
      setSearchNonce((n) => n + 1);
    } catch { /* surfaced by the panel below */ }
    finally { setReassigning(false); }
  }, [reassign, allChars, dispatch, litGroupUuid, loadSuggestions, loadLog]);

  /** Put a hand-reassigned line back to the speaker it had. The counterpart Unmerge has always had. */
  const doUndoReassign = useCallback(async (l, bookUuid) => {
    setReassigning(true);
    try {
      await apiClient.post(`/voice-rendering/${bookUuid}/undo-reassign`, { lineIds: [l.lineId] });
      dispatch(fetchLitGroupCast(litGroupUuid));
      loadSuggestions();
      loadLog();
      // Same staleness as a reassign, in the other direction.
      setSearchNonce((n) => n + 1);
    } catch { /* surfaced by the panel */ }
    finally { setReassigning(false); }
  }, [dispatch, litGroupUuid, loadSuggestions, loadLog]);

  const renderPassageLine = (l, i, bookUuid) => {
    const narration = isNarration(l);
    const text = l.content || '';
    const clipped = text.length > 320 ? `${text.slice(0, 320)}…` : text;
    if (narration) {
      // Descriptive glue — muted, no speaker chip; this is where the "…said X, turning
      // to the Nevergate keeper" identity cues live.
      return (
        <div key={i} style={{ margin: '0.15em 0', color: '#8a8a8a', fontStyle: 'italic', lineHeight: 1.4 }}>
          {clipped}
        </div>
      );
    }
    return (
      <div key={i} style={{
        margin: '0.2em 0', lineHeight: 1.4,
        background: l.isTarget ? '#fff8e1' : 'transparent',
        borderLeft: l.isTarget ? '3px solid #f2c200' : '3px solid transparent',
        paddingLeft: '0.5em',
      }}>
        <span style={{ color: l.isTarget ? '#8a6d00' : '#777', fontSize: '0.85em', fontWeight: l.isTarget ? 700 : 400 }}>
          {(l.speaker || '—')}:{' '}
        </span>
        <span style={{ color: l.isTarget ? '#5c4a00' : '#333' }}>{clipped}</span>
        {/*
          ⭐ REASSIGN, ON THE LINE ITSELF (2026-08-15). A merge answers "these two labels are one
          person"; a reassign answers "THIS line belongs to someone else", and until now only the
          first had a home in this tool. The second is what a container label needs — `unknown` is
          54 lines belonging to dozens of people, and `the old man` is a different man in each of
          three books, so no single merge can be right for either. It is also simply how you fix one
          wrong attribution on an otherwise correct character.
          ⚠️ Never merge a pronoun: the alias would claim that string for every future line, and
          ScriptLinker resolves aliases first-come in alphabetical order, so the winner is arbitrary.
        */}
        {/*
          ⚠️ THE TARGET LINE GETS A LABELLED BUTTON, NOT A BARE ICON (2026-08-15). The first cut used
          a small grey icon trailing the text on every line, matching the caret elsewhere — and the
          operator, who had asked for this feature and knew it existed, could not find it. A caret
          sits at a predictable place; this sat at the end of a variable-length sentence in the same
          grey as the punctuation. The line you are looking at is the one you almost always want, so
          it says what it does; the surrounding lines keep the quiet icon for the rarer case of
          fixing a neighbour.
        */}
        {l.lineId && bookUuid && (l.isTarget ? (
          <Button size="mini" compact basic color="blue"
            style={{ marginLeft: '0.6em', padding: '0.3em 0.6em', verticalAlign: 'middle' }}
            onClick={() => { setReassign({ lineId: l.lineId, bookUuid, text: clipped, was: l.speaker || '' }); setReassignTo(null); }}
            title="Bind this one line to a different character">
            <Icon name="exchange" /> Reassign this line
          </Button>
        ) : (
          <Icon link name="exchange" size="small" style={{ color: '#b0b0b0', marginLeft: '0.4em' }}
            title="This line belongs to someone else — reassign it"
            onClick={() => { setReassign({ lineId: l.lineId, bookUuid, text: clipped, was: l.speaker || '' }); setReassignTo(null); }} />
        ))}
        {/*
          The receipt, shown where the change was made. `speakerWas` was being recorded from the
          first version of this feature and read by nothing, while the panel promised "Reversible" —
          a claim about the data that was not yet true of the tool.
        */}
        {l.speakerWas && (
          <span style={{ marginLeft: '0.6em', fontSize: '0.78em', color: '#8a6d3b' }}>
            was <em>{l.speakerWas}</em>
            <Button size="mini" compact basic style={{ marginLeft: '0.4em', padding: '0.2em 0.45em' }}
              loading={reassigning} disabled={reassigning || !bookUuid}
              onClick={() => doUndoReassign(l, bookUuid)}
              title={`Put this line back to "${l.speakerWas}"`}>
              <Icon name="undo" /> undo
            </Button>
          </span>
        )}
        {/*
          ⛔ THE PICKER CANNOT LIVE IN THIS ROW (2026-09-18). It used to, and the operator reported the
          menu "needs to draw above over its container and the list table". Semantic positions a
          Dropdown's menu absolutely INSIDE its own row, and every row here sits in a box with
          `maxHeight` + `overflowY: auto` — 36vh for the text-search hits, 48vh for the cast list — so
          the scroll box CLIPS the menu and neighbouring rows paint over what survives. No z-index
          fixes that; the menu has to leave the scroll box, and a Modal is portaled to the document
          body, so it cannot be clipped by anything.

          ⭐ The TRIGGER stays on the line, which was always the right call — the decision is made
          with the sentence in front of you. Only the menu moves.
        */}
      </div>
    );
  };

  const renderContext = (c) => {
    const entry = contextByChar[c.uuid];
    const panelStyle = {
      marginTop: '0.5em', marginLeft: '2.4em', paddingLeft: '0.85em',
      borderLeft: '3px solid #e6e6e6',
    };
    if (!entry || entry.loading) {
      return <div style={panelStyle}><Loader active inline size="tiny" /> <span style={{ color: '#999' }}>loading context…</span></div>;
    }
    if (entry.error) {
      return <div style={panelStyle}><span style={{ color: '#9f3a38' }}>Couldn't load context: {entry.error}</span></div>;
    }
    if (!entry.passages.length) {
      return <div style={panelStyle}><span style={{ color: '#999' }}>No dialogue attributed to this name.</span></div>;
    }
    return (
      <div style={panelStyle}>
        {entry.passages.map((p, pi) => (
          <div key={pi} style={{ marginBottom: '0.9em' }}>
            {p.bookTitle && (
              /* ⭐ Book AND chapter (2026-10-09) — see CharacterLinesPanel for why. The occurrence
                 list below already showed "ch N"; this label, which is the one an operator reads
                 while deciding whether two names are one person, did not. */
              <Label size="mini" style={{ marginBottom: '0.35em' }} title="book and chapter">
                {p.bookTitle}{p.chapter != null ? ` · ch ${p.chapter}` : ''}
              </Label>
            )}
            {p.lines.map((l, li) => renderPassageLine(l, li, p.bookUuid))}
          </div>
        ))}
        {entry.total > entry.passages.length && (
          <div style={{ color: '#999', fontSize: '0.85em', marginTop: '0.3em' }}>
            showing {entry.passages.length} of {entry.total} occurrences
            {/*
              Browsing, as opposed to confirming. The panel was built to answer "is this the same
              person" from three samples, and that is a different job from working through a
              character's lines — which is what an operator does when the character IS the problem.
              Without this, a 3,519-line Narrator could only ever be read from the top.
            */}
            <Button size="mini" compact basic style={{ marginLeft: '0.6em', padding: '0.2em 0.5em' }}
              disabled={busy}
              onClick={() => dispatch(fetchCharacterContext({
                litGroupUuid, characterUuid: c.uuid, q: textQ,
                samples: Math.min(entry.passages.length + 12, 60),
              }))}
              title="Load more of this character's lines">
              show more
            </Button>
            {entry.total > 60 && !textQ && (
              <span style={{ marginLeft: '0.5em' }}>
                — for this many, switch the box to <strong>Line text</strong> and search for a phrase
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  /**
   * One line in a result list — used by both the text search and the reassignment log.
   *
   * Shows the neighbours, because the operator's own use is to search NARRATION ("instructed the
   * monitors") in order to identify the speaker of the DIALOGUE beside it. A bare hit would find the
   * evidence and hide the thing it is evidence for.
   */
  const renderResultLine = (l) => {
    const isOpen = reassign && reassign.lineId === l.lineId;
    const neighbour = (x, label) => (x && x.content ? (
      <div style={{ color: '#9a9a9a', fontSize: '0.78em', fontStyle: 'italic' }}>
        {label} {x.speaker ? `${x.speaker}: ` : ''}{x.content}
      </div>
    ) : null);
    return (
      <div key={l.lineId} style={{ borderTop: '1px solid #eee', padding: '0.5em 0' }}>
        <div style={{ fontSize: '0.78em', color: '#999' }}>
          {l.bookTitle} · ch {l.chapter ?? '–'} · {l.type}
        </div>
        {neighbour(l.before, '↑')}
        <div style={{ margin: '0.15em 0' }}>
          <strong style={{ color: '#00695c' }}>{l.speaker || '—'}:</strong>{' '}
          <span>{l.content.length > 300 ? `${l.content.slice(0, 300)}…` : l.content}</span>
          {l.speakerWas && (
            <span style={{ marginLeft: '0.5em', fontSize: '0.78em', color: '#8a6d3b' }}>
              was <em>{l.speakerWas}</em>
              <Button size="mini" compact basic style={{ marginLeft: '0.35em', padding: '0.2em 0.45em' }}
                loading={reassigning} disabled={reassigning}
                onClick={() => doUndoReassign(l, l.bookUuid)}
                title={`Put this line back to "${l.speakerWas}"`}>
                <Icon name="undo" /> undo
              </Button>
            </span>
          )}
          <Button size="mini" compact basic color="blue"
            style={{ marginLeft: '0.5em', padding: '0.25em 0.55em' }}
            disabled={reassigning}
            onClick={() => { setReassign({ lineId: l.lineId, bookUuid: l.bookUuid, text: l.content, was: l.speaker }); setReassignTo(null); }}
            title="Bind this line to a different character">
            <Icon name="exchange" /> Reassign
          </Button>
        </div>
        {neighbour(l.after, '↓')}
        {/*
          ⛔ THE PICKER USED TO BE HERE TOO, and this is the copy the operator would have hit first:
          the text search is the fast route to a line, and its hits render in a 36vh scroll box. Same
          clipping, same cause — see `reassignDialog`. The armed line is marked instead, so the row
          still shows WHICH line the open dialog is about.
        */}
        {isOpen && (
          <div style={{ marginTop: '0.3em', fontSize: '0.8em', color: '#2185d0' }}>
            <Icon name="arrow up" /> being reassigned — choose who it belongs to in the dialog
          </div>
        )}
      </div>
    );
  };

  /**
   * ⛔ NEVER JOIN CAST NAMES WITH ", " (2026-09-18). A speaker label in this corpus can CONTAIN a
   * comma — "friendly, masculine voice" is one machine — so a comma-separated list of names is
   * ambiguous by construction. After a merge, the row read:
   *
   *     merged: voice, friendly, masculine voice
   *
   * Two names shown as three fragments, and nothing to say which. That is how an unintended member
   * of a merge stays invisible AFTER the fact: the operator folded a ch49 label into a ch33 machine
   * and the receipt could not report it legibly. The ConfirmModal already renders its items one per
   * line for the same reason — "in a native confirm these collapse into a paragraph and stop being
   * readable at exactly the moment they matter most."
   *
   * Each name becomes its own chip, so the delimiter is a box rather than a character that names are
   * allowed to contain.
   */
  const nameChips = (names) => (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '0.25em', verticalAlign: 'middle' }}>
      {names.map((n, i) => (
        <span key={`${n}-${i}`}
          style={{
            background: '#f0f0f0', border: '1px solid #e0e0e0', borderRadius: 3,
            padding: '0 0.35em', color: '#666', whiteSpace: 'nowrap',
          }}>{n}</span>
      ))}
    </span>
  );

  /** The same rule for a plain-text tooltip, where chips are not available. */
  const quotedNames = (names) => names.map((n) => `“${n}”`).join(' · ');

  const renderRow = (c) => {
    const checked = selected.includes(c.uuid);
    const locked = isProtected(c);
    const isExpanded = expanded.includes(c.uuid);
    return (
      <List.Item key={c.uuid} style={{ padding: '0.55em 0.25em' }}>
        <List.Content floated="right">
          {/* Chapters in the tooltip as well as on the confirmation, so the tell is available while
              you are CHOOSING members and not only when you commit. */}
          {/* ⭐ When a book is picked the badge shows lines IN THAT BOOK, since that is the number
              the decision rests on; the tooltip keeps the corpus total so nothing is hidden. */}
          {c.dialogueCount > 0 && (
            <Label basic size="small" color={bookFiltered ? 'teal' : 'blue'}
              title={bookFiltered
                ? `${linesInBook(c)} line${linesInBook(c) === 1 ? '' : 's'} in this book · ${c.dialogueCount} across the group`
                : (c.chapters?.length
                  ? `${c.dialogueCount} dialogue line${c.dialogueCount === 1 ? '' : 's'} · ch ${c.chapters.join(', ')}`
                  : 'dialogue lines')}>
              <Icon name="comment" />{bookFiltered ? linesInBook(c) : c.dialogueCount}
            </Label>
          )}
          {c.aliases?.length > 0 && (
            <Label basic size="small" title={`already merges: ${quotedNames(c.aliases)}`}>
              <Icon name="linkify" />{c.aliases.length}
            </Label>
          )}
          {locked && (
            <Label size="small" color={c.hasRecording ? 'green' : 'teal'}
              title={c.hasRecording ? 'has a recorded voice — protected' : 'assigned to a voice actor — protected'}>
              <Icon name="lock" />{c.hasRecording ? 'recorded' : 'assigned'}
            </Label>
          )}
        </List.Content>
        <List.Content>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5em' }}>
            <Icon link name={isExpanded ? 'caret down' : 'caret right'}
              onClick={() => toggleExpand(c.uuid)}
              style={{ color: '#888' }}
              title={isExpanded ? 'hide surrounding context' : 'read surrounding context'} />
            <Checkbox
              checked={checked}
              disabled={locked || busy}
              onChange={() => toggle(c.uuid)}
              label={
                <label style={{ cursor: locked ? 'not-allowed' : 'pointer' }}>
                  <strong>{c.name || <em>(unnamed)</em>}</strong>
                  {c.dialogueCount === 0 && (
                    <span style={{ color: '#999', marginLeft: 6, fontSize: '0.9em' }}>· no dialogue</span>
                  )}
                </label>
              }
            />
          </span>
          {c.mergedVariants?.length > 0 ? (
            <div style={{ marginLeft: '2.4em', marginTop: '0.1em', fontSize: '0.85em', color: '#8a8a8a' }}>
              <Icon name="linkify" size="small" style={{ color: '#b0b0b0' }} />
              merged: {nameChips(c.mergedVariants.map((v) => v.name || prettyAlias(v.uuid)))}
              {' '}
              {/*
                A REAL BUTTON, because this is the only way to undo a merge (2026-08-10). It was a
                0.85em grey <span> that turned blue on hover and, when `busy`, silently became inert
                while still LOOKING like a link — an operator clicked it, nothing happened, and
                nothing said why. A Button renders its own disabled state, takes keyboard focus for
                free, and reads as an action rather than as part of the sentence above it.
              */}
              <Button
                size="mini"
                basic
                compact
                icon
                labelPosition="left"
                disabled={busy}
                onClick={() => handleUnmerge(c)}
                title="Split these variants back out"
                style={{ marginLeft: '0.6em' }}
              >
                <Icon name="expand" />Unmerge
              </Button>
            </div>
          ) : c.aliases?.length > 0 ? (
            <div style={{ marginLeft: '2.4em', marginTop: '0.1em', fontSize: '0.85em', color: '#8a8a8a' }}>
              <Icon name="linkify" size="small" style={{ color: '#b0b0b0' }} />
              also: {nameChips(c.aliases.map(prettyAlias))}
            </div>
          ) : null}
          {/*
            ⭐ WHERE ITS LINES WENT (2026-08-15, operator's suggestion: leave a reassigned entry
            visible, but marked). A character emptied by reassignment and one that never spoke both
            read "0 lines" — identical on screen, opposite in meaning. One is a decision already
            taken; the other is an artefact nobody has looked at. Saying so is the difference between
            a list you can trust and a list you have to re-derive from memory.
          */}
          {c.reassignedTo?.length > 0 && (
            <div style={{ marginLeft: '2.4em', marginTop: '0.1em', fontSize: '0.85em', color: '#5d8aa8' }}>
              <Icon name="exchange" size="small" style={{ color: '#8fb3c7' }} />
              reassigned to: {nameChips(c.reassignedTo.map((v) => `${v.name}${v.lines > 1 ? ` (${v.lines} lines)` : ''}`))}
            </div>
          )}
          {/*
            ⭐ THE REFUSALS THIS CHARACTER CARRIES, and the only way to take one back — the mirror of
            the Unmerge affordance above, in the same place and the same shape. Before 2026-08-15 a
            "not the same" was invisible the moment it was clicked and unrecoverable after a reload;
            now it is stated, and each one is individually reversible. Per-entry rather than
            all-or-nothing, because a character can be refused against several others independently.
          */}
          {c.rejectedWith?.length > 0 && (
            <div style={{ marginLeft: '2.4em', marginTop: '0.1em', fontSize: '0.85em', color: '#8a8a8a' }}>
              <Icon name="unlinkify" size="small" style={{ color: '#c0a0a0' }} />
              not the same as:{' '}
              {c.rejectedWith.map((v, i) => (
                <span key={v.uuid}>
                  {i > 0 && ', '}
                  {v.name || prettyAlias(v.uuid)}
                  <Icon link name="undo" size="small" disabled={busy}
                    onClick={() => undoRejection(c, v)}
                    style={{ color: '#b0b0b0', marginLeft: '0.25em' }}
                    title={`Undo — allow "${c.name}" and "${v.name}" to be suggested again`} />
                </span>
              ))}
            </div>
          )}
          {isExpanded && renderContext(c)}
        </List.Content>
      </List.Item>
    );
  };

  /**
   * One proposed grouping — used by BOTH the engine's suggestions and the advisory spelling-variant
   * list, so the controls a reader learns in one place work identically in the other.
   *
   * `advisory` changes the framing only, never the mechanics: the radio still picks the surviving
   * name, ✕ still records a durable refusal, and Merge still folds. What it must not do is let an
   * advisory row read as something the engine endorsed — `Victoria` / `Viktoria` sits in that list
   * and is two different women.
   */
  /**
   * ONE HEADING FOR ALL THREE PANELS, with the expand control in the same place every time.
   *
   * The unevaluated panel already had a show/hide inline after its title; the other two had none,
   * so a long cast meant scrolling past every suggestion to reach the next section. Operator asked
   * for the control top-right on each panel, which also stops the three drifting apart: a shared
   * heading cannot end up with the toggle in three positions.
   *
   * The blurb takes the remaining width and the button is pinned to the top-right corner, so a
   * heading that wraps to two lines does not drag the control down with it.
   */
  const panelHead = ({ icon, iconColor, title, count, blurb, open, onToggle, titleColor }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5em', marginBottom: '0.4em' }}>
      <Icon name={icon} color={iconColor} style={iconColor ? undefined : { color: '#888' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong style={titleColor ? { color: titleColor } : undefined}>{title} ({count})</strong>
        {blurb && (
          <span style={{ color: '#888', fontSize: '0.9em' }}> {blurb}</span>
        )}
      </div>
      <Button
        size="mini" basic compact
        style={{ marginLeft: 'auto', flex: '0 0 auto' }}
        onClick={onToggle}
        title={open ? 'Collapse this panel' : 'Expand this panel'}
      >
        <Icon name={open ? 'chevron up' : 'chevron down'} style={{ margin: 0 }} />
        {open ? ' hide' : ' show'}
      </Button>
    </div>
  );

  const renderGroupRow = (g, advisory = false) => {
    const canon = sugCanonical[g.key] || g.canonicalUuid;
    const canonName = (g.members.find((m) => m.uuid === canon) || {}).name || 'canonical';
    const openMembers = g.members.filter((m) => expanded.includes(m.uuid));
    const accent = advisory ? '#8a6d3b' : '#00695c';
    return (
      <div key={g.key} style={{ borderTop: '1px solid #eee', padding: '0.5em 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            {g.members.map((m) => (
              <span key={m.uuid} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3em', marginRight: '1.1em' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3em', cursor: 'pointer' }}
                  title={m.uuid === canon ? 'canonical — the name that is kept' : 'merges into the canonical (kept as an alias)'}>
                  <input type="radio" name={`canon-${g.key}`} checked={m.uuid === canon}
                    disabled={mergingSug === g.key}
                    onChange={() => setSugCanonical((s) => ({ ...s, [g.key]: m.uuid }))} />
                  <strong style={{ color: m.uuid === canon ? accent : '#444' }}>{m.name}</strong>
                  <span style={{ color: '#999', fontSize: '0.82em' }}>
                    · {m.lines} line{m.lines === 1 ? '' : 's'}{m.gender && m.gender !== 'unknown' ? ` · ${m.gender}` : ''}
                  </span>
                  {(m.clones > 0 || m.assigned > 0) && (
                    <Label size="mini" color="green" title={m.clones > 0 ? 'recorded — protected' : 'assigned to a VA — protected'}>
                      <Icon name="lock" />{m.clones > 0 ? 'recorded' : 'assigned'}
                    </Label>
                  )}
                </label>
                {/* Context peek — reuses the cast row's lines+narration expander to confirm identity. */}
                <Icon link name={expanded.includes(m.uuid) ? 'caret down' : 'caret right'}
                  onClick={() => toggleExpand(m.uuid)} style={{ color: '#888' }}
                  title={expanded.includes(m.uuid) ? 'hide lines & context' : 'read this name’s lines & surrounding narration to confirm'} />
                {/*
                  ⭐ EXEMPT ONE MEMBER (2026-08-15, operator's request): "in cases of > 2 merge
                  members, it would be nice to exempt 1 or more members from the merge, and merge the
                  rest." One icon on a chip that already carries a caret — no new mode, and the group
                  visibly shrinks as its own feedback. Hidden on the canonical, which is the thing
                  everything else merges INTO.
                */}
                {m.uuid !== canon && (
                  <Icon link name="close" size="small"
                    disabled={busy || !!mergingSug || !!rejectingSug}
                    loading={rejectingSug === `${g.key}:${m.uuid}`}
                    onClick={() => rejectMember(g, m)}
                    style={{ color: '#c0392b', marginLeft: '-0.15em' }}
                    title={`Not this one — record that "${m.name}" is a different character from the rest of this ${advisory ? 'pair' : 'suggestion'}`} />
                )}
              </span>
            ))}
            {advisory && g.reason && (
              <div style={{ color: '#8a6d3b', fontSize: '0.8em', marginTop: '0.15em' }}>{g.reason}</div>
            )}
          </div>
          <Button.Group size="mini">
            {/*
              SAY WHAT IT WILL DO, NOT WHAT IT IS. The count matters once members can be exempted —
              "Merge 3 into Eldest" confirms a partial merge before it runs, which is the ToC spec's
              "show consequences, not classifications" applied to a button label.
            */}
            <Button color={advisory ? 'yellow' : 'teal'} loading={mergingSug === g.key}
              disabled={busy || !!mergingSug || !!rejectingSug}
              onClick={() => mergeSuggestion(g, canon)}
              title={advisory
                ? `Read both first — then fold "${g.members.find((m) => m.uuid !== canon)?.name}" into "${canonName}"`
                : `Fold the other ${g.members.length - 1} into "${canonName}"`}>
              <Icon name="compress" /> Merge {g.members.length} into {canonName.length > 22 ? canonName.slice(0, 22) + '…' : canonName}
            </Button>
            <Button basic loading={rejectingSug === g.key}
              disabled={busy || !!mergingSug || !!rejectingSug}
              onClick={() => rejectWholeGroup(g, canon)}
              title={advisory
                ? 'Different characters that merely look alike — recorded, so it is not raised again'
                : 'None of these belong together — recorded, so it is not offered again'}>
              Not the same
            </Button>
          </Button.Group>
        </div>
        {openMembers.length > 0 && (
          <div style={{ textAlign: 'right', marginTop: '0.2em' }}>
            <Icon link name="close" style={{ color: '#999' }}
              title="Close the open context for this suggestion"
              onClick={() => setExpanded((prev) => prev.filter((u) => !g.members.some((m) => m.uuid === u)))} />
          </div>
        )}
        {openMembers.map((m) => (
          <div key={m.uuid} style={{ marginTop: '0.4em' }}>
            <div style={{ marginLeft: '2.4em', fontSize: '0.85em', color: accent }}>{m.name}</div>
            {renderContext(m)}
          </div>
        ))}
      </div>
    );
  };

  const body = (
    <>
      <p style={{ color: '#666', marginTop: 0 }}>
        Check the name-variants of one character, pick the <strong>real</strong> one to
        merge them into, and click <strong>Merge</strong>. Or check spurious entries and{' '}
        <strong>Dismiss</strong> them. Recorded / assigned characters are locked and can
        only be a merge destination.
      </p>

      {/* Guided Merge — name-containment suggestions shown together (no page-hunting), one click to
            merge into the canonical. Editable canonical (radio); "Not the same" hides the card. */}
      {(() => {
        // No local filter any more — a refused pairing is simply not in `suggestions`.
        const visible = suggestions;
        if (!visible.length) return null;
        return (
          <Segment color="teal" style={{ marginBottom: '0.75em' }}>
            {panelHead({
              icon: 'magic', iconColor: 'teal', title: 'Suggested merges', count: visible.length,
              blurb: '— the same character split across name variants; pick the name to keep, then Merge',
              open: showSuggestions, onToggle: () => setShowSuggestions((v) => !v),
            })}
            {showSuggestions && visible.map((g) => renderGroupRow(g))}
          </Segment>
        );
      })()}

      {/*
          ⭐ NAMES ONE CHARACTER APART — advisory, and deliberately NOT in the list above.
          Containment cannot propose these: "cavvittoy" is not a subset of "cavvitoy" and shares no
          token with it, so the engine is structurally blind to the whole class (operator, 2026-08-15,
          having merged a misspelled Cavvitoy by hand). Sized before building: 27 pairs across six
          groups, with `Lisbeth` (3 lines) beside `Lizbeth` (147) in FOUR of them, and a
          `Polisan-dra Fenderwell` whose line-break hyphen reached a speaker label.

          ⚠️ SEPARATE BLOCK, WEAKER LANGUAGE, ON PURPOSE. The counter-example is inside the band it
          reports — `Victoria` and `Viktoria` are one letter apart and two different women, and the
          containment engine is RIGHT to keep them apart. So this proposes nothing: it says "these
          look like one name", and ✕ answers it for good.
        */}
      {nearMisses.length > 0 && (
        <Segment color="yellow" style={{ marginBottom: '0.75em' }}>
          {panelHead({
            icon: 'question circle outline', iconColor: 'yellow',
            title: 'Possible spelling variants', count: nearMisses.length,
            blurb: '— one character apart, which the merge engine cannot see. NOT a suggestion: read both names’ lines before merging, or ✕ to record that they are different people.',
            open: showNearMisses, onToggle: () => setShowNearMisses((v) => !v),
          })}
          {showNearMisses && nearMisses.map((g) => renderGroupRow(g, true))}
        </Segment>
      )}

      {/**
          * ⭐ WHAT THE ENGINE REFUSED TO GUESS — and why this section exists at all.
          *
          * Measured on LG Corpus AI after the Ghost import: the engine formed 7 clusters, proposed
          * 3, and discarded 4 of them — 507 lines — with no trace in the response. Add the
          * shared-name refusals and it was 806 lines of real decisions that reached nobody. The only
          * route to them was reading all 182 names, which is the work this panel exists to remove.
          *
          * ⛔ NOT a suggestion, and the distinction is load-bearing. Of those four discarded
          * clusters exactly TWO deserved it — Diogenes + QPack bridged by the joke label "Mister
          * Diogenes QPack", and Diana + the Brain bridged by "Miles, Diana and the Brain" — and two
          * were one person each, Goss + Shavvender and Fortinbras + Jorik. Half and half, from
          * strings alone. So the guards stay, the engine keeps refusing, and the operator decides.
          *
          * ⚠️ Each row carries the server's own words for why it was withheld, because "these share
          * no words with each other" is the fact that makes the decision, and paraphrasing it here
          * would let the two drift.
          */}
      {withheld.length > 0 && (
        <Segment color="orange" style={{ marginBottom: '0.75em' }}>
          {panelHead({
            icon: 'hand paper outline', iconColor: 'orange',
            title: 'Shared names the engine will not guess about', count: withheld.length,
            blurb: '— these names overlap, but merging them could be wrong, so nothing is proposed. Pick the surviving name and Merge if they are one character, or ✕ to record that they are not. The pre-selected name is just the most-spoken one, not a recommendation.',
            open: showWithheld, onToggle: () => setShowWithheld((v) => !v),
          })}
          {showWithheld && withheld.map((g) => (

            <div key={g.key}>
              <div style={{ fontSize: '0.84em', color: '#8a5300', padding: '0.35em 0 0' }}>
                <Icon name={g.reason === 'bridged' ? 'linkify' : 'users'} />
                {g.why}
              </div>
              {renderGroupRow(g, true)}
            </div>
          ))}
        </Segment>
      )}

      {/*
          ⭐ THE NAMES NO RULE CAN EVER REACH (2026-08-15). getMergeSuggestions has always computed
          and returned `unevaluated`, and this screen has always thrown it away — so a character
          whose every word is a title (`Baron`, `she`, `Her mother`) was invisible here no matter how
          many lines it held. The server's own comment names this screen as its destination.

          ⚠️ TWO CLASSES, OPPOSITE ADVICE, WHICH IS WHY THEY ARE LABELLED SEPARATELY.
          A title is specific enough to merge — "baron" as a bare speaker label almost certainly is
          him. A PRONOUN is not: merging writes it as an alias, and ScriptLinker resolves aliases
          first-come in alphabetical order, so every future line saying "she" would bind to whichever
          canonical sorts earliest. That is arbitrary arbitration of a correctness question, so a
          pronoun is reassigned line by line and never merged.

          This block never empties — `Glendyl's father` is a possessive forever — so it is collapsed
          and counted rather than presented as an unfinished queue.
        */}
      {/* olive: the only read-only panel here — nothing in it is an action. */}
      {unevaluated.length > 0 && (
        <Segment color="olive" style={{ marginBottom: '0.75em' }}>
          {panelHead({
            icon: 'eye slash outline', title: 'The engine cannot evaluate these',
            count: unevaluated.length, titleColor: '#555',
            blurb: '— every word is a title, or the name is possessive. No suggestion will ever include them.',
            open: showUneval, onToggle: () => setShowUneval((v) => !v),
          })}
          {showUneval && (
            <div style={{ marginTop: '0.5em' }}>
              {['title-or-stopword only', 'possessive'].map((reason) => {
                const rows = unevaluated.filter((u) => u.reason === reason);
                if (!rows.length) return null;
                return (
                  <div key={reason} style={{ marginTop: '0.4em' }}>
                    <div style={{ fontSize: '0.8em', color: '#777', borderTop: '1px solid #eee', paddingTop: '0.35em' }}>
                      {/*
                          ⚠️ THE POSSESSIVE HEADING SAID "nothing to do" WHILE EVERY ROW BENEATH IT
                          SAID "choose who it belongs to" (2026-08-16). The operator reassigned
                          `Her father` into `Glendyl's father` — correctly — and then found
                          `Glendyl's father` sitting in this list looking like it was owed the same
                          treatment, with no candidate to give it to. For that character there is
                          none; the book never names him.

                          ⚠️ AND THE FIRST FIX OVERREACHED, which the operator also caught by asking
                          whether the text was fabricated from book data. It said "the book gives no
                          other" — a claim about THEIR corpus, hard-coded, rendered over every
                          possessive, and verified for exactly one of them by a human reading it.

                          ⚠️ THE SECOND FIX THEN HARD-CODED A CHARACTER NAME. It explained the rule
                          with "Glendyl's mother is not Glendyl" — a real person from this operator's
                          books, baked into a string that will ship to authors for whom the name
                          means nothing. The rule is general and needed no example: the rows rendered
                          directly beneath ARE the examples, drawn from whatever cast is loaded.

                          The standing rule this leaves: corpus specifics belong in comments like
                          this one, where they record why a decision was made. USER-VISIBLE copy gets
                          only what is true of every book, because nothing tests a string.
                        */}
                      {reason === 'possessive'
                        ? 'POSSESSIVE — the engine will not treat “X’s Y” as a variant of X: a possessive names somebody OTHER than its owner. Often these are complete names in their own right, and a good voice brief; whether the book names this person elsewhere is something only you can say.'
                        : 'REDUCES TO A TITLE — merge by hand if it belongs to someone. ⚠ A PRONOUN is reassigned line by line instead, never merged.'}
                    </div>
                    {rows.map((u) => {
                      const pronoun = /^(he|she|they|him|her|them|it|his|hers|its)$/i.test(String(u.name).trim());
                      // The cast list knows where a reassigned entry's lines went; this block is
                      // rendered from the suggestions payload, so borrow it rather than duplicate.
                      const moved = allChars.find((x) => x.uuid === u.uuid)?.reassignedTo || [];
                      return (
                        <div key={u.uuid} style={{ display: 'flex', alignItems: 'center', gap: '0.5em', padding: '0.2em 0' }}>
                          <span style={{ color: '#999', fontSize: '0.82em', minWidth: 74 }}>
                            {u.lines} line{u.lines === 1 ? '' : 's'}
                          </span>
                          <strong style={{ color: pronoun ? '#c0392b' : '#444', minWidth: 170 }}>{u.name}</strong>
                          {moved.length > 0 ? (
                            <span style={{ fontSize: '0.8em', color: '#5d8aa8' }}>
                              <Icon name="exchange" size="small" />
                              reassigned to {nameChips(moved.map((v) => `${v.name}${v.lines > 1 ? ` (${v.lines})` : ''}`))}
                              {u.lines === 0 && ' — nothing left to decide'}
                            </span>
                          ) : pronoun ? (
                            <span style={{ fontSize: '0.8em', color: '#c0392b' }}>
                              pronoun — open its lines and reassign each one
                            </span>
                          ) : (
                            // ⚠️ A CHECKBOX, NOT A "Select" BUTTON (2026-08-15). The button only
                            // ADDED — clicking again did nothing and there was no way to undo it
                            // from here — and its only feedback was the action bar further down
                            // the page, possibly below the fold. The cast rows already use a
                            // checkbox for precisely this selection, so it shows its own state,
                            // toggles both ways, and needs no explaining.
                            <Checkbox
                              checked={selected.includes(u.uuid)}
                              disabled={busy}
                              onChange={() => toggle(u.uuid)}
                              label={
                                <label style={{ fontSize: '0.8em', color: '#666' }}>
                                  {/*
                                      The same control, but it must not make the same PROMISE in both
                                      classes. A bare title is probably somebody — "Baron" is almost
                                      certainly Baron Eveready — so the invitation is to go and find
                                      them. A possessive already IS somebody, so selecting it is the
                                      exception, not the expected next step.
                                    */}
                                  {reason === 'possessive'
                                    ? (u.lines === 0
                                      ? 'no dialogue — select to dismiss'
                                      : 'select ONLY if the book names this person somewhere else')
                                    : 'select — then choose who it belongs to in the bar below'}
                                </label>
                              }
                            />
                          )}
                          <Icon link name={expanded.includes(u.uuid) ? 'caret down' : 'caret right'}
                            style={{ color: '#888' }} onClick={() => toggleExpand(u.uuid)}
                            title="read its lines" />
                        </div>
                      );
                    })}
                    {rows.filter((u) => expanded.includes(u.uuid)).map((u) => (
                      <div key={`ctx-${u.uuid}`}>{renderContext(u)}</div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </Segment>
      )}

      {/* Action bar — kept OUTSIDE the scrolling list so the "merge into" dropdown
            menu opens over the list instead of being clipped by its overflow. */}
      {/**
          * violet: the WORKING surface, where characters are actually selected and merged, as
          * against the advisory panels above it (teal proposes, yellow guesses, orange refuses,
          * olive is inert). Not blue — that is the text-search result panel, which NESTS inside this
          * one, and not green, which already means "recorded/assigned, protected" on a member label.
          */}
      <Segment color="violet" style={{ marginBottom: '0.75em' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75em', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, textAlign: 'left', color: '#666' }}>
            {selected.length > 0
              ? `${selected.length} selected`
              : 'Select characters to merge or dismiss'}
          </span>

          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4em' }}>
            <span style={{ color: '#666' }}>merge into</span>
            <Dropdown
              selection
              search
              disabled={selected.length === 0 || busy || canonicalOptions.length === 0}
              options={canonicalOptions}
              value={canonicalUuid}
              onChange={(e, { value }) => setCanonicalUuid(value)}
              placeholder="canonical…"
              style={{ minWidth: 220 }}
            />
            <Button primary icon labelPosition="left"
              disabled={!canMerge} loading={loading.merge} onClick={handleMerge}>
              <Icon name="compress" />Merge
            </Button>
          </span>

          <Button color="orange" icon labelPosition="left"
            disabled={!canDismiss} loading={loading.dismiss} onClick={handleDismiss}
            title={canonicalName ? undefined : 'Removes the selected profiles'}>
            <Icon name="trash alternate" />Dismiss
          </Button>
        </div>
      </Segment>

      {error && (
        <Message negative onDismiss={() => dispatch(clearCastFeedback())}>
          <Message.Header><Icon name="warning sign" />{error}</Message.Header>
          {blocked?.length > 0 && (
            <List bulleted style={{ marginTop: 8 }}>
              {blocked.map((b) => (
                <List.Item key={b.uuid}>
                  <strong>{b.name}</strong>{' '}
                  {b.recorded ? '(has a recorded voice)' : ''}{b.recorded && b.assigned ? ' ' : ''}
                  {b.assigned ? '(assigned to a VA)' : ''}
                </List.Item>
              ))}
            </List>
          )}
        </Message>
      )}

      {lastResult && (
        <Message positive onDismiss={() => dispatch(clearCastFeedback())}>
          <Icon name="check circle" />
          {lastResult.kind === 'merge'
            ? `Merged ${lastResult.mergedCount} into "${lastResult.canonName}".`
            : lastResult.kind === 'unmerge'
              ? `Unmerged ${lastResult.unmergedCount} variant${lastResult.unmergedCount === 1 ? '' : 's'} back out.`
              : `Dismissed ${lastResult.dismissedCount} entr${lastResult.dismissedCount === 1 ? 'y' : 'ies'}.`}
          {/*
              SAY WHAT THE MERGE DECIDED ABOUT SEX. The survivor's gender used to be inherited
              silently from whichever node was canonical — and canonical is picked by line count,
              which has no opinion about sex. It is now RE-DERIVED over the merged lines, but tiers
              beyond the label are still inference, and this value chooses the cloned voice. So it
              is stated rather than left to be discovered in the roster afterwards.
            */}
          {lastResult.kind === 'merge' && lastResult.gender?.gender && (
            <div style={{ marginTop: '0.4em', fontSize: '0.95em' }}>
              <Icon name={lastResult.gender.gender === 'female' ? 'venus' : lastResult.gender.gender === 'male' ? 'mars' : 'genderless'} />
              {lastResult.gender.source === 'locked' ? (
                <>Gender kept as <strong>{lastResult.gender.gender}</strong> — you set this, so it was not re-derived.</>
              ) : (
                <>
                  Gender re-derived as <strong>{lastResult.gender.gender}</strong>
                  {lastResult.gender.source === 'label'
                    ? ' from its name or an absorbed alias'
                    : ' from the surrounding narration'}
                  {lastResult.gender.changed && lastResult.gender.before
                    ? ` — changed from ${lastResult.gender.before}.`
                    : '.'}
                  {lastResult.gender.source !== 'label' && ' This is inferred — correct it if you know better.'}
                </>
              )}
            </div>
          )}
        </Message>
      )}

      {loading.fetch ? (
        <Segment basic>
          <Loader active inline="centered">Loading cast…</Loader>
        </Segment>
      ) : allChars.length === 0 ? (
        <Message info>No characters found for this series yet.</Message>
      ) : (
        <>
          {/*
              ⭐ ONE INPUT, TWO MODES (2026-08-16). Filtering by NAME cannot answer "who says this
              line?", and that is the question an operator has while reading. Worse, the context
              panel takes `hits[0..samples]` — the FIRST few — so the Narrator's 3,855 lines could
              only ever show the opening of one book. There was no way to reach line 2,000.
              In `text` mode the same box searches the group's lines AND retargets every context
              panel to window around matches, which is what makes a large character navigable at all.
              The mode is an explicit button rather than a prefix convention: this tool has produced
              three invisible-affordance defects in two days, and a clever syntax would be a fourth.
            */}
          {/**
              * BOOK FILTER + the post-ingestion view.
              *
              * Defaults to All books. After an ingestion the operator switches to the new book and
              * ticks "new & unvoiced", which answers the question an import actually raises: which
              * characters do I have to cast? For Ghost that was 83 characters and 437 lines.
              *
              * The counts sit in the option text because they are the reason to pick one, and the
              * line badge on each row switches to this book when a book is chosen -- a corpus total
              * does not describe the work in front of you.
              */}
          {books.length > 1 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75em', marginBottom: '0.75em' }}>
              {/* A visible label, because a bare dropdown among the other controls does not say
                    what it filters — and this one changes what the list MEANS, not just its order. */}
              <strong style={{ whiteSpace: 'nowrap', fontSize: '0.92em' }}>Book Filter</strong>
              <Dropdown
                selection compact search
                style={{ minWidth: 240 }}
                value={bookFilter}
                onChange={(_, d) => setBookFilter(d.value)}
                options={[
                  { key: 'all', value: ALL_BOOKS, text: `All books (${allChars.length})` },
                  ...books.map((b) => ({
                    key: b.uuid,
                    value: b.uuid,
                    text: `${b.title} (${allChars.filter((c) => (c.bookLines || []).some((x) => x.bookId === b.uuid)).length})`,
                  })),
                ]}
              />
              {bookFiltered && (
                <Checkbox
                  toggle
                  label="new &amp; unvoiced only"
                  checked={newUnvoicedOnly}
                  onChange={(_, d) => setNewUnvoicedOnly(!!d.checked)}
                />
              )}
              {/*
                ⭐ CAST WHAT THE FILTER IS SHOWING. Placed here, in the filter bar, because the filter
                IS the selection — "new & unvoiced in this book" is the question an ingestion raises,
                and re-ticking that same set in another panel is how a 73-character pass gets
                abandoned half-done.

                ⚠️ Offered only when a book is chosen. Across all books the set is the whole corpus's
                cast, which is not a casting decision anybody means to make in one click.
              */}
              {bookFiltered && filteredReal.length > 0 && (
                <Button
                  size="tiny"
                  basic
                  color="teal"
                  icon
                  labelPosition="left"
                  loading={castBusy}
                  disabled={castBusy}
                  onClick={openCastVoices}
                  style={{ marginLeft: 'auto' }}
                >
                  <Icon name="microphone" />
                  Audition {filteredReal.length} voice{filteredReal.length === 1 ? '' : 's'}
                </Button>
              )}
            </div>
          )}
          <Input
            icon={false} fluid size="small"
            placeholder={mode === 'name' ? 'Filter by name…' : 'Search the text of every line…'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ marginBottom: '0.75em' }}
            action
          >
            <Button
              basic compact size="small" style={{ whiteSpace: 'nowrap' }}
              onClick={() => { setMode((m) => (m === 'name' ? 'text' : 'name')); setQuery(''); }}
              title={mode === 'name'
                ? 'Switch to searching what characters SAY'
                : 'Switch to filtering the cast by name'}
            >
              <Icon name={mode === 'name' ? 'user' : 'align left'} />
              {mode === 'name' ? 'Name' : 'Line text'}
            </Button>
            <input />
            <Button
              basic compact size="small" icon="close" disabled={!query}
              onClick={() => setQuery('')}
              title="Clear"
            />
          </Input>

          <Menu tabular size="small">
            <Menu.Item active={activeTab === 'cast'} onClick={() => switchTab('cast')}>
              <Icon name="users" />Cast
              <Label circular size="mini" color={activeTab === 'cast' ? 'blue' : undefined}>{filteredReal.length}</Label>
            </Menu.Item>
            <Menu.Item active={activeTab === 'nodialogue'} onClick={() => switchTab('nodialogue')}>
              <Icon name="question circle outline" />Phantoms
              <Label circular size="mini" color={activeTab === 'nodialogue' ? 'blue' : undefined}>{filteredSuspect.length}</Label>
            </Menu.Item>
            {/*
                The session's own audit trail. Every one of these was a judgement call with no rule
                behind it, and until now the only way back to one was remembering where you put it.
              */}
            {/*
                ⭐ A HOME FOR REASSIGNMENT (2026-09-18). Every affordance for it already existed —
                the dual search above, the per-line button, the log below — and the operator still
                could not find where to START: "it is buried in the actual text, which is good for
                final resolution but not good for finding where to do Reassignment." So this tab adds
                no machinery. It names the job and says which search answers it. Third
                invisible-affordance defect in this tool, and the first fixed by signposting rather
                than by moving the control.
              */}
            <Menu.Item active={activeTab === 'reassign'} onClick={() => switchTab('reassign')}>
              <Icon name="exchange" />Reassign
            </Menu.Item>
            <Menu.Item active={activeTab === 'reassigned'} onClick={() => switchTab('reassigned')}>
              <Icon name="history" />Reassigned
              <Label circular size="mini" color={activeTab === 'reassigned' ? 'blue' : undefined}>{log.length}</Label>
            </Menu.Item>
            {/*
              ⭐ THE AUDITIONS SIT BESIDE THE LIST THAT GENERATED THEM. "Audition N voices" is pressed
              on the Cast tab, and the takes it produces are reviewed one tab over — rather than on
              another page, which is how a 73-character casting pass gets left half-listened-to.

              ⚠️ The count is deliberately absent. It would need its own fetch on every open of this
              modal to be honest, and a stale badge on a casting queue is worse than no badge.
            */}
            <Menu.Item active={activeTab === 'auditions'} onClick={() => switchTab('auditions')}>
              <Icon name="microphone" />Auditions
            </Menu.Item>
          </Menu>

          {/*
            ⭐ THE RUN, MADE VISIBLE. Above the tabs rather than inside the Auditions tab, because
            the run outlives any one tab and an operator who wanders back to the cast list should
            still be able to see that work is happening.

            ⚠️ An indeterminate bar when the stream is blind, a real percentage when it is not —
            never a 0% bar that will not move, which reads as a hung job.
          */}
          {castRun && (
            <Segment
              color={castRun.error ? 'red' : castRun.done ? 'green' : 'teal'}
              style={{ marginTop: '0.75em', marginBottom: 0, padding: '0.6em 0.9em' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                <Icon
                  name={castRun.error ? 'warning sign' : castRun.done ? 'check circle' : 'microphone'}
                  color={castRun.error ? 'red' : castRun.done ? 'green' : 'teal'}
                  loading={!!castRun.active && !castRun.error}
                />
                <strong style={{ fontSize: '0.95em' }}>
                  {castRun.error ? String(castRun.error) : castRun.message}
                </strong>
                {castRun.active && castRun.expected > 0 && (
                  <span style={{ color: '#888', fontSize: '0.85em' }}>
                    — {castRun.expected} character{castRun.expected === 1 ? '' : 's'}, 0 operations
                  </span>
                )}
                {castRun.done && (
                  <Button
                    size="mini" basic compact style={{ marginLeft: 'auto' }}
                    onClick={() => setCastRun(null)}
                  >
                    Dismiss
                  </Button>
                )}
              </div>
              {castRun.active && (
                <Progress
                  size="tiny"
                  indicating
                  percent={typeof castRun.percent === 'number' ? castRun.percent : 100}
                  style={{ margin: '0.5em 0 0' }}
                />
              )}
              {castRun.capMessage && (
                <Message size="tiny" warning style={{ marginTop: '0.5em' }}>
                  <Icon name="warning sign" /> ElevenLabs stopped the run: {castRun.capMessage}
                </Message>
              )}
              {/*
                ⚠️ A REFUSED BRIEF IS NOT A FAILED RUN. Everybody else was auditioned; these
                characters were skipped and are named so they can be fixed. The remedy is stated
                because it is not guessable — the brief is the thing to change, and it is editable.
              */}
              {!!castRun.refused?.length && (
                <Message size="tiny" warning style={{ marginTop: '0.5em' }}>
                  <Icon name="ban" />
                  <strong>
                    {castRun.refused.length} brief{castRun.refused.length === 1 ? '' : 's'} refused by
                    ElevenLabs
                  </strong>
                  <div style={{ marginTop: '.3em' }}>
                    {castRun.refused.map((x) => (
                      <div key={x.uuid} style={{ marginBottom: '.2em' }}>
                        · {x.name || x.uuid}
                        {x.prompt && (
                          <Button
                            size="mini" basic compact style={{ marginLeft: '.4em' }}
                            onClick={() => setRefusedOpen(refusedOpen === x.uuid ? null : x.uuid)}
                          >
                            {refusedOpen === x.uuid ? 'hide' : 'show'} the brief that was refused
                          </Button>
                        )}
                        {refusedOpen === x.uuid && (
                          <div style={{
                            fontSize: '12px', marginTop: '.25em', whiteSpace: 'pre-wrap',
                            opacity: 0.9, borderLeft: '3px solid #d9a', paddingLeft: '.5em',
                          }}>
                            {x.prompt}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '.3em', opacity: 0.85 }}>
                    Their brief tripped ElevenLabs' content filter. Everyone else was auditioned.
                    ⭐ Open <strong>Audition</strong> again, press <strong>Read the prompts
                      first</strong>, and rewrite the brief for {castRun.refused.length === 1 ? 'this character' : 'these characters'} —
                    then audition {castRun.refused.length === 1 ? 'them' : 'them'} on their own.
                  </div>
                  {castRun.refused[0]?.message && (
                    <div style={{ marginTop: '.3em', fontSize: '11.5px', opacity: 0.75 }}>
                      ElevenLabs said: {castRun.refused[0].message}
                    </div>
                  )}
                </Message>
              )}
              {/* Other skips are failures of input, not of content — a different fix each. */}
              {!!castRun.skipped?.filter((x) => x.reason !== 'brief-refused-by-provider').length && (
                <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '.4em' }}>
                  <Icon name="info circle" />
                  {' '}also skipped: {castRun.skipped
                    .filter((x) => x.reason !== 'brief-refused-by-provider')
                    .map((x) => `${x.name || x.uuid} (${x.reason})`).join(', ')}
                </div>
              )}
            </Segment>
          )}

          {/*
              Text-mode results sit ABOVE the tabs' list rather than replacing it: the cast list is
              still the thing being curated, and a search is a lens onto it, not a different screen.
            */}
          {mode === 'text' && query.trim().length >= 2 && (
            <Segment color="blue" style={{ marginTop: '0.75em', marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginBottom: '0.3em' }}>
                <Icon name="search" color="blue" />
                <strong>
                  {searching ? 'Searching…'
                    : hits ? `${hits.total} line${hits.total === 1 ? '' : 's'} contain “${query.trim()}”` : ''}
                </strong>
                {hits && hits.total > hits.lines.length && (
                  <span style={{ color: '#888', fontSize: '0.85em' }}>
                    — showing the first {hits.lines.length}; narrow the search to see the rest
                  </span>
                )}
              </div>
              <div style={{ maxHeight: '36vh', overflowY: 'auto' }}>
                {hits && hits.lines.length === 0 && !searching && (
                  <Message size="small" info>Nothing matches. Search is over the words of every line, including narration.</Message>
                )}
                {hits && hits.lines.map(renderResultLine)}
              </div>
            </Segment>
          )}

          <div style={{ maxHeight: '48vh', overflowY: 'auto', paddingRight: '0.25em', marginTop: '0.75em' }}>
            {activeTab === 'auditions' ? (
              /*
                ⭐ Mounted only while the tab is open, so it fetches its queue (and presigns three
                preview URLs per character) on demand rather than on every open of this modal.
                `onCast` refreshes the cast beneath, because a character that has just been given a
                voice is no longer "new & unvoiced" and should leave that filter.
              */
              <VoiceCandidateReview
                litGroupUuid={litGroupUuid}
                onCast={() => {
                  dispatch(fetchLitGroupCast(litGroupUuid));
                  if (onVoiceCast) onVoiceCast();
                }}
                /* ⭐ So an empty queue during a run says "auditioning" rather than "nothing here". */
                runActive={!!(castRun && castRun.active)}
                runMessage={castRun && castRun.active ? castRun.message : null}
                /* Bumped on CASTING_COMPLETE — the one moment there is certainly something new. */
                refreshNonce={castNonce}
                /* A resample started inside the panel is the same background job; watch it the same way. */
                onRunStarted={watchCastRun}
              />
            ) : activeTab === 'reassigned' ? (
              log.length === 0 ? (
                <Message info size="small">
                  No lines have been reassigned in this group yet. Reassignments appear here so a decision
                  can be revisited without remembering where it went.
                </Message>
              ) : (
                <div>{log.map(renderResultLine)}</div>
              )
            ) : activeTab === 'reassign' ? (
              <>
                {/*
                    The two routes in, in the order they are useful: you usually know the WORDS of the
                    line, because you have just read or heard it, and only sometimes the character.
                    Both use the one search box above — this tab does not own a second one, and the
                    list below it is the same cast list, so nothing here is a separate screen to keep
                    in step.
                  */}
                <Message info size="small" style={{ marginTop: 0 }}>
                  <Message.Header>Move one line to a different character</Message.Header>
                  <p style={{ marginBottom: '0.4em' }}>
                    Use the search above. <strong>Line text</strong> finds a line by its own words —
                    the fastest route when you have just read or heard it. <strong>Name</strong>{' '}
                    filters the cast below; open a character to see its lines.
                  </p>
                  <p style={{ margin: 0 }}>
                    Then click <Icon name="exchange" fitted /> on the line. A reassignment moves that
                    line only and is reversible. It never claims two names are one person — that is
                    what a merge does.
                  </p>
                </Message>
                {filteredReal.length === 0 ? (
                  <Message size="small">
                    No cast members{query ? ` match “${query}”` : ' with dialogue yet'}.
                  </Message>
                ) : (
                  <List divided relaxed="very">
                    {realPageItems.map(renderRow)}
                  </List>
                )}
              </>
            ) : activeTab === 'cast' ? (
              filteredReal.length === 0 ? (
                <Message info size="small">No cast members{query ? ` match “${query}”` : ' with dialogue yet'}.</Message>
              ) : (
                <List divided relaxed="very">
                  {realPageItems.map(renderRow)}
                </List>
              )
            ) : (
              <>
                <p style={{ color: '#999', fontSize: '0.9em', marginTop: 0 }}>
                  No dialogue attributed — likely phantoms or spurious import artifacts (a title, a blurb author, "Unknown").
                </p>
                {filteredSuspect.length === 0 ? (
                  <Message info size="small">Nothing here{query ? ` matches “${query}”` : ''}.</Message>
                ) : (
                  <List divided relaxed>
                    {suspectPageItems.map(renderRow)}
                  </List>
                )}
              </>
            )}
          </div>

          {/* Pager — always in the same spot below the list, for whichever tab is active. */}
          {(activeTab === 'cast' || activeTab === 'reassign') && realPages > 1 && (
            <div style={{ textAlign: 'center', marginTop: '0.75em' }}>
              <Pagination size="mini" activePage={safeRealPage} totalPages={realPages}
                siblingRange={1} boundaryRange={1}
                onPageChange={(e, { activePage }) => setRealPage(activePage)} />
            </div>
          )}
          {activeTab === 'nodialogue' && suspectPages > 1 && (
            <div style={{ textAlign: 'center', marginTop: '0.75em' }}>
              <Pagination size="mini" activePage={safeSuspectPage} totalPages={suspectPages}
                siblingRange={1} boundaryRange={1}
                onPageChange={(e, { activePage }) => setSuspectPage(activePage)} />
            </div>
          )}
        </>
      )}
    </>
  );

  /**
   * ⭐ THE CONFIRMATION MUST EXIST IN BOTH RENDER MODES (2026-08-10).
   *
   * This used to live only inside the <Modal> branch below, while `inline` returned `body` and
   * stopped — so in the Green Room, which renders this component inline, the dialog was never in
   * the tree at all. Clicking Unmerge set `confirm`, React re-rendered, and nothing appeared: no
   * dialog, no dimmer, no error. Dismiss was dead the same way, for the same reason.
   *
   * Both are confirm-gated, so in the inline surface BOTH ACTIONS SILENTLY DID NOTHING. A button
   * that quietly does nothing is worse than one that fails, because there is nothing to report.
   *
   * Declared once and rendered by both branches so the two modes cannot drift apart again.
   */
  const confirmDialog = (
    <>
      {/*
      ⭐ THE PREFLIGHT, SHOWN RATHER THAN SUMMARISED.

      ⛔ `selectCastTargets` drops a character with no `suggestedArchetype` and reports nothing, so
      without this an operator auditions forty and gets twelve with no explanation — and the remedy
      is not guessable from the symptom. So every blocked character is listed WITH ITS REASON,
      because the remedies differ: `no-derivation-yet` means run Update Casting, while
      `has-a-real-recording` means leave that character alone permanently.

      ⚠️ NOT A DANGER DIALOG. `danger={false}` on purpose: this step spends no voice operations, and
      dressing a free action in red teaches the operator to click through red. The spend happens one
      character at a time, later, after listening.
    */}
      <ConfirmModal
        open={!!castPreflight}
        title="Audition these voices?"
        icon="microphone"
        danger={false}
        confirmLabel={
          castPreflight && !castPreflight.error && castWillRun > 0
            // ⛔ Counts what WILL run, not what is eligible. The two differ by the already-voiced set,
            // and a button that promised 83 and auditioned 61 is how the replacement bug stayed
            // invisible for a whole run.
            ? `Audition ${Math.min(castWillRun, castPreflight.perRunCap || castWillRun)}`
            : 'Close'
        }
        onCancel={() => { setCastPreflight(null); setCastDryRun(null); setCastEdits({}); }}
        onConfirm={() => {
          if (!castPreflight || castPreflight.error || !castWillRun) return setCastPreflight(null);
          return confirmCastVoices();
        }}
      >
        {castPreflight?.error ? (
          <Message negative size="tiny"><Icon name="warning sign" /> {String(castPreflight.error)}</Message>
        ) : (
          <div style={{ fontSize: '14px', lineHeight: 1.5 }}>
            <p>
              ElevenLabs returns <strong>three</strong> voices for each character. Nothing is bought
              here — you listen first, then choose one per character.
            </p>
            <p style={{ margin: '.5em 0' }}>
              <Icon name="check circle" color="green" />
              <strong>{castWillRun}</strong> will be auditioned.
            </p>
            {/*
            ⭐ WHO, BY NAME. The dialog used to give a number for the characters being auditioned and
            a list only for the ones being skipped — so the set actually about to be worked on was
            the one thing not shown.
          */}
            {!!(castPreflight?.eligibleList || []).length && (
              <div style={{ maxHeight: 150, overflowY: 'auto', fontSize: '13px', margin: '.4em 0' }}>
                {(castPreflight.eligibleList || [])
                  .filter((x) => castIncludeReplacing || !x.replacing)
                  .map((x) => (
                    <div key={x.uuid} style={{ marginBottom: '.15em' }}>
                      <strong>{x.name || x.uuid}</strong>
                      <span style={{ opacity: 0.75 }}>
                        {x.lines ? ` — ${x.lines} line${x.lines === 1 ? '' : 's'}` : ''}
                        {x.archetype ? ` · ${x.archetype}` : ''}
                      </span>
                      {x.replacing && (
                        <Label size="mini" color="orange" style={{ marginLeft: '.4em' }}>replaces a voice</Label>
                      )}
                    </div>
                  ))}
              </div>
            )}

            {/*
            ⛔ THE ALREADY-VOICED SET, HELD BACK BY DEFAULT.

            These pass every eligibility test, so the first client sent them along and the operator
            opened a queue full of characters they had already cast. Replacing a voice is a separate
            decision — it discards one chosen by ear and costs 2 operations to confirm — so it is a
            tick, not a side effect of leaving "new & unvoiced only" off.
          */}
            {castPreflight?.replacing > 0 && (
              <Segment style={{ padding: '.6em .8em', margin: '.5em 0' }}>
                <Checkbox
                  checked={castIncludeReplacing}
                  onChange={(_, d) => setCastIncludeReplacing(!!d.checked)}
                  label={`Also re-audition the ${castPreflight.replacing} character`
                    + `${castPreflight.replacing === 1 ? '' : 's'} that already `
                    + `${castPreflight.replacing === 1 ? 'has' : 'have'} a voice`}
                />
                <div style={{ fontSize: '12.5px', opacity: 0.8, marginTop: '.3em' }}>
                  ⚠️ Casting one of those replaces the voice already chosen for that character.
                  {!castIncludeReplacing && ' Left off, they are skipped entirely.'}
                </div>
                {castIncludeReplacing && (castPreflight.replacingList || []).length > 0 && (
                  <div style={{ fontSize: '12.5px', opacity: 0.85, marginTop: '.3em' }}>
                    {(castPreflight.replacingList || []).map((x) => x.name || x.uuid).join(', ')}
                  </div>
                )}
              </Segment>
            )}
            {/* ⭐ Stated in operations, not vaguely as "free" — the ration is the thing the operator
              budgets, and 2 per voice against 1,040 a month is the number that matters. */}
            {/* <p style={{ margin: '.5em 0', opacity: 0.85 }}>
              <Icon name="dont" /> <strong>0 voice operations</strong> now. Committing a voice later
              costs 2 each, out of 1,040 a month.
            </p> */}
            {/*
            ⭐ THE PROMPTS, BEFORE ANY AUDIO EXISTS. Offered rather than forced: it costs an LLM call
            and a minute of reading, which is worth it when the composer has just been changed and
            wasted when it has not.
          */}
            <Segment style={{ padding: '.6em .8em', margin: '.5em 0', borderTopColor: 'blue', borderTopWidth: '3px' }}>
              {castWillRun > 0 && (
                <div style={{ margin: '.5em 0' }}>

                  {!castDryBusy && (
                    <>
                      <Button
                        size="mini" basic icon labelPosition="left"
                        // loading={castDryBusy}
                        disabled={castDryBusy}
                        onClick={() => runCastDryRun(castRunIds)}
                      >
                        <Icon name="eye" />
                        {castDryRun && !castDryRun.error ? 'Re-read the prompts' : 'Read the prompts first'}
                      </Button>
                      <span style={{ fontSize: '12px', opacity: 0.75, marginLeft: '.5em' }}>
                        composes them without making audio
                      </span>
                    </>
                  )}


                  {/*
                ⭐ A STOP, because this is minutes long and the briefs already composed are kept.
                ⚠️ It stops the LOOP, not an in-flight request: the batch being composed finishes
                and is rendered, which is both cheaper to implement honestly and better behaviour —
                an LLM call already paid for should not be discarded on its way home.
              */}
                  {castDryBusy && (
                    <Table style={{ border: "none" }}>
                      <Table.Row>
                        <Table.Cell>
                          <Button size="mini" basic compact onClick={() => { castDryAbort.current = true; }}>
                            <Icon name="stop" /> Stop after this batch
                          </Button></Table.Cell>
                        <Table.Cell>
                          <span style={{ textAlign: 'left', fontSize: '12px', opacity: 0.75, marginLeft: '.5em' }}>
                            Stopping may take a few seconds...
                          </span>
                        </Table.Cell>
                      </Table.Row>
                    </Table>
                  )}
                </div>
              )}


              {/*
            ⛔ A MULTI-MINUTE WAIT NEEDS A COUNT. 61 briefs is ~16 composing calls; a spinner alone
            is indistinguishable from a hang, and this is long enough that the operator will
            reasonably start wondering which it is.
          */}

              {castDryProgress && (
                <div style={{ fontSize: '12.5px', margin: '.35em 0' }}>
                  {castDryProgress.stopped ? (
                    <>
                      <Icon name="stop circle" />
                      {' '}stopped — {castDryProgress.done} of {castDryProgress.total} composed, and kept.
                    </>
                  ) : (
                    <>
                      {/* <Icon name="circle notched" loading /> */}
                      <Divider />
                      <Table style={{ border: "none" }}>
                        <Table.Row>
                          <Table.Cell><Spinner size="1.3rem" /></Table.Cell>
                          <Table.Cell>
                            {' '}composing batch {castDryProgress.batch} of {castDryProgress.of} —
                            {' '}{castDryProgress.done} of {castDryProgress.total} written.
                            {/* ⭐ Said once, because it is the reason the wait is acceptable. */}
                            <span style={{ opacity: 0.75 }}>
                              {' '}Scene partners are composed together, so they can be told apart.
                            </span>
                          </Table.Cell>
                        </Table.Row>
                      </Table>
                    </>
                  )}
                </div>
              )}
            </Segment>
            {castDryRun?.error && (
              <Message negative size="tiny"><Icon name="warning sign" /> {String(castDryRun.error)}</Message>
            )}
            {!!(castDryRun?.proposals || []).length && (
              <div style={{ maxHeight: 280, overflowY: 'auto', margin: '.4em 0' }}>
                {castDryRun.proposals.map((pr) => (
                  <Segment key={pr.uuid} style={{ padding: '.5em .7em', margin: '0 0 .4em' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '.4em', flexWrap: 'wrap' }}>
                      <strong>{pr.name}</strong>
                      <span style={{ fontSize: '12px', opacity: 0.75 }}>
                        {pr.gender || '?'}{pr.age ? ` · ${pr.age}` : ''}
                        {pr.archetype ? ` · ${pr.archetype}` : ''}
                      </span>
                      {pr.promptSource === 'operator' && (
                        <Label size="mini" color="teal"><Icon name="pencil" /> yours</Label>
                      )}
                      {/* ⚠️ The prompt's LENGTH is a quality signal: the hand-written briefs in
                        docs/journal run 800–900 characters, and the composer is capped far shorter. */}
                      <Label size="mini" basic style={{ marginLeft: 'auto' }}>{pr.promptChars} chars</Label>
                      <Button
                        size="mini" basic compact
                        onClick={() => setCastEditing({
                          uuid: pr.uuid,
                          name: pr.name,
                          prompt: pr.prompt || '',
                          sampleText: pr.sampleText || '',
                        })}
                      >
                        <Icon name="pencil" /> Edit
                      </Button>
                    </div>
                    <div style={{ fontSize: '12.5px', marginTop: '.3em', whiteSpace: 'pre-wrap' }}>
                      {pr.prompt || '(no prompt composed)'}
                    </div>
                    {/*
                    ⭐ WHAT THE BRIEF WAS WRITTEN FROM. A brief that misses the character is usually
                    a brief that was handed the wrong evidence, and the two commonest causes are
                    visible here: no emotion tags at all, or a scene partner the composer could not
                    see. Reading the prompt alone cannot distinguish a bad writer from a blind one.
                  */}
                    {pr.evidence && (
                      <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '.35em' }}>
                        <Icon name="database" size="small" />
                        {' '}read {pr.evidence.linesRead} of {pr.evidence.lineCount} line
                        {pr.evidence.lineCount === 1 ? '' : 's'}
                        {pr.evidence.chapters?.length
                          ? ` · ch ${pr.evidence.chapters.slice(0, 6).join(', ')}${pr.evidence.chapters.length > 6 ? '…' : ''}`
                          : ''}
                        {/* ⛔ No tags means the range half of the brief was written from nothing. */}
                        {pr.evidence.emotionsTagged > 0 ? (
                          <div style={{ marginTop: '.15em' }}>
                            <Icon name="sliders horizontal" size="small" />
                            {' '}range: {pr.evidence.emotions.slice(0, 6)
                              .map((e) => `${e.emotion} ×${e.count}`).join(', ')}
                            {pr.evidence.emotions.length > 6 ? '…' : ''}
                          </div>
                        ) : (
                          <div style={{ marginTop: '.15em' }}>
                            <Icon name="warning sign" color="orange" size="small" />
                            {' '}no emotion tags on their lines — the brief could not be written from
                            their range
                          </div>
                        )}
                        {/*
                        ⭐ THE LINE THAT MATTERS ON A FIRST RUN. `neighbours` needs voices that
                        already exist, which a single-book group cast in one pass has none of.
                        `coPresent` is who shares their chapters in THIS run — the brief is written
                        to differ from those whether or not anybody is cast yet, because there is no
                        later book coming to improve a voice.
                      */}
                        {!!pr.evidence.coPresent?.length && (
                          <div style={{ marginTop: '.15em' }}>
                            <Icon name="users" size="small" />
                            {' '}told apart from {pr.evidence.coPresent
                              .map((n) => `${n.name} (${n.shared} ch)`).join(', ')}
                            {pr.evidence.coPresentTotal > pr.evidence.coPresent.length
                              ? ` +${pr.evidence.coPresentTotal - pr.evidence.coPresent.length} more`
                              : ''}
                          </div>
                        )}
                        {!!pr.evidence.neighbours?.length && (
                          <div style={{ marginTop: '.15em' }}>
                            <Icon name="history" size="small" />
                            {' '}and from voices already cast: {pr.evidence.neighbours
                              .map((n) => `${n.name} (${n.shared} ch)`).join(', ')}
                          </div>
                        )}
                        {/* ⚠️ A character who shares no chapter with anybody being cast cannot
                          collide — worth saying, so a missing line is not read as a missing input. */}
                        {!pr.evidence.coPresent?.length && !pr.evidence.neighbours?.length && (
                          <div style={{ marginTop: '.15em', opacity: 0.8 }}>
                            <Icon name="user" size="small" />
                            {' '}shares no chapter with another character being cast
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '.3em', fontStyle: 'italic' }}>
                      {pr.sampleSource === 'stock'
                        ? <><Icon name="warning sign" color="orange" /> stock text — no usable line: </>
                        : pr.sampleSource === 'own-then-stock'
                          ? (
                            <>
                              <Icon name="warning sign" color="yellow" />
                              {' '}their line, then stock
                              {pr.sampleOwnChars ? ` (${pr.sampleOwnChars} chars theirs)` : ''}
                              {': '}
                            </>
                          )
                          : <><Icon name="quote left" size="small" /> their own words: </>}
                      {String(pr.sampleText || '').slice(0, 140)}
                      {String(pr.sampleText || '').length > 140 ? '…' : ''}
                    </div>
                  </Segment>
                ))}
              </div>
            )}

            {castWillRun > (castPreflight?.perRunCap || 0) && (
              <Message size="tiny" warning>
                <Icon name="warning sign" />
                Only {castPreflight.perRunCap} are auditioned per run — {castWillRun - castPreflight.perRunCap} will
                be left for a second pass.
              </Message>
            )}
            {!!(castPreflight?.blocked || []).length && (
              <>
                <div style={{ fontWeight: 600, margin: '.75em 0 .25em' }}>
                  {castPreflight.blocked.length} will be skipped:
                </div>
                <div style={{ maxHeight: 180, overflowY: 'auto', fontSize: '13px' }}>
                  {castPreflight.blocked.map((b) => (
                    <div key={b.uuid} style={{ marginBottom: '.2em' }}>
                      <strong>{b.name || b.uuid}</strong>
                      <span style={{ opacity: 0.8 }}> — {CAST_BLOCK_REASONS[b.reason] || b.reason}</span>
                    </div>
                  ))}
                </div>
                {castPreflight.blocked.some((b) => b.reason === 'no-derivation-yet') && (
                  <Message size="tiny" info style={{ marginTop: '.5em' }}>
                    <Icon name="info circle" />
                    Run <strong>Update Casting</strong> first and these will become available — it is
                    what sets the archetype a voice prompt is built from.
                  </Message>
                )}
              </>
            )}
          </div>
        )}
      </ConfirmModal>
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        items={confirm?.items}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger !== false}
        icon={confirm?.danger === false ? 'sitemap' : 'warning sign'}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.kind === 'unmerge') return runUnmerge(confirm.character);
          if (confirm?.kind === 'merge') return runMerge();
          return runDismiss();
        }}
      >
        {confirm?.body}
      </ConfirmModal>
      {/*
      ⭐ THE PROMPT EDITOR — the operator's text supersedes the composer's.

      ⛔ NOT A DANGER DIALOG and not confirmed on close: writing a voice brief is the work, not a
      risky act, and `Discard` simply leaves the composed prompt in place.

      ⚠️ TWO FIELDS, because they fail differently. The PROMPT decides what kind of voice comes
      back; the SAMPLE decides what it says while you judge it, and a character with no line over
      100 characters is reading stock text — which is exactly where pasting a real line from the
      book is worth more than any rewording of the prompt.

      ⭐ `Recreate` discards the edit and asks the composer again. Composition runs warm, so it is a
      fresh roll rather than a promise of different text — said plainly on the button's note so a
      second identical prompt does not read as a broken button.
    */}
      <ConfirmModal
        open={!!castEditing}
        title={castEditing ? `The voice brief for ${castEditing.name}` : 'Edit the brief'}
        icon="pencil"
        danger={false}
        size="large"
        confirmLabel="Use this brief"
        cancelLabel="Discard"
        onCancel={() => setCastEditing(null)}
        onConfirm={() => {
          if (!castEditing) return;
          const next = {
            ...castEdits,
            [castEditing.uuid]: {
              prompt: castEditing.prompt,
              sampleText: castEditing.sampleText,
            },
          };
          setCastEdits(next);
          setCastEditing(null);
          // ⭐ Re-read immediately with the edit applied, so the card shows the words that will be
          // sent rather than the ones that were replaced.
          runCastDryRun([castEditing.uuid], next, { merge: true });
        }}
      >
        {castEditing && (
          <div style={{ fontSize: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '.5em', marginBottom: '.25em' }}>
              <strong>The brief ElevenLabs is given</strong>
              <span style={{ fontSize: '12px', opacity: 0.75 }}>
                {castEditing.prompt.trim().length} characters
                {/* ⚠️ Not a limit — a yardstick. The briefs that produced the voices the operator kept
                  are this long, and the composer's are not. */}
                {castEditing.prompt.trim().length < 400
                  ? ' — the hand-written briefs in docs/journal run 800–900'
                  : ''}
              </span>
              {castEditing.prompt.trim().length < 20 && (
                <Label size="mini" color="orange">too short to use</Label>
              )}
            </div>
            <TextArea
              value={castEditing.prompt}
              onChange={(_, d) => setCastEditing((e) => ({ ...e, prompt: d.value }))}
              rows={9}
              style={{ width: '100%', fontFamily: 'inherit', fontSize: '13px', lineHeight: 1.45 }}
            />
            <div style={{ fontSize: '12px', opacity: 0.8, margin: '.3em 0 .9em' }}>
              Describe only PERMANENT qualities — timbre, register, apparent age, pace, accent,
              bearing. ⛔ Never a passing mood: the line's own emotion is applied at render time, and a
              voice fixed in one mood plays every line in it, including the calm ones.
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: '.5em', marginBottom: '.25em' }}>
              <strong>What the voice reads while you judge it</strong>
              <span style={{ fontSize: '12px', opacity: 0.75 }}>
                {castEditing.sampleText.trim().length} characters
              </span>
              {castEditing.sampleText.trim().length < 100 && (
                <Label size="mini" color="orange">
                  under 100 — Voice Design will refuse it, so the automatic pick is used
                </Label>
              )}
            </div>
            <TextArea
              value={castEditing.sampleText}
              onChange={(_, d) => setCastEditing((e) => ({ ...e, sampleText: d.value }))}
              rows={4}
              style={{ width: '100%', fontFamily: 'inherit', fontSize: '13px', lineHeight: 1.45 }}
            />
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '.3em' }}>
              ⭐ The character's own words judge a voice better than a stock paragraph. Paste a real
              line — one that exercises the quality most at risk, not simply the longest.
            </div>

            <div style={{ marginTop: '.9em', display: 'flex', alignItems: 'center', gap: '.6em' }}>
              <Button
                size="mini" basic
                loading={castDryBusy}
                disabled={castDryBusy}
                onClick={() => {
                  // Drop this character's edit, then re-compose just them.
                  const next = { ...castEdits };
                  delete next[castEditing.uuid];
                  setCastEdits(next);
                  setCastEditing(null);
                  runCastDryRun([castEditing.uuid], next, { merge: true });
                }}
              >
                <Icon name="redo" /> Recreate from the automation
              </Button>
              <span style={{ fontSize: '12px', opacity: 0.75 }}>
                throws away this edit and asks the composer again — a fresh roll, so it may come back
                similar. 0 voice operations.
              </span>
            </div>
          </div>
        )}
      </ConfirmModal>
    </>
  );

  /**
   * ⛔ WHERE THE "belongs to…" PICKER LIVES NOW (2026-09-18) — and it has to be OUT of the list.
   *
   * Inside the row it was clipped: the cast list and the search hits are boxes with `maxHeight` +
   * `overflowY: auto`, and Semantic renders a Dropdown's menu absolutely inside its own row, so the
   * scroll box cut the options off and later rows painted over them. A Modal portals to the document
   * body, so nothing above it in the tree can clip or outrank it.
   *
   * ⚠️ Declared beside `confirmDialog` and rendered by BOTH branches, for the reason written there:
   * a dialog that exists only in the Modal branch is silently absent from the Green Room's inline
   * surface, and a control that quietly does nothing is worse than one that fails.
   */
  const reassignDialog = (
    <Modal open={!!reassign} size="tiny" closeOnDimmerClick={!reassigning}
      onClose={() => { if (!reassigning) { setReassign(null); setReassignTo(null); setReassignFilter(''); } }}
      style={{ zIndex: 10020 }} dimmer={{ style: { zIndex: 10010 } }}>
      <Modal.Header><Icon name="exchange" /> Reassign this line</Modal.Header>
      <Modal.Content>
        {/* The line itself, so the decision is still made against the words rather than a lineId. */}
        <div style={{ fontStyle: 'italic', color: '#333', marginBottom: '0.6em' }}>
          “{reassign?.text}”
        </div>
        <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '0.8em' }}>
          This line only — <strong>{reassign?.was || '(no speaker)'}</strong> keeps every other line.
          Reversible; the original label is kept.
        </div>
        {/*
          ⛔ NO DROPDOWN. A third clipping report killed it: inside the row the scroll box cut the
          menu off, and inside this Modal the content box does the same — Semantic's dropdown menu is
          absolutely positioned and something in every ancestor chain here clips it. Chasing that with
          overflow and z-index overrides is a fight with the framework that the next container will
          restart.

          ⭐ So the chooser IS the content. This Modal exists for exactly one decision, so the list of
          characters can simply be in it: a filter box and a scrolling list of names. Nothing pops
          over anything, so nothing can be clipped, by this container or any future one.
        */}
        <Input
          fluid size="small" icon="search" iconPosition="left"
          placeholder="Filter the cast…"
          value={reassignFilter}
          onChange={(e) => setReassignFilter(e.target.value)}
          disabled={reassigning}
          style={{ marginBottom: '0.5em' }}
        />
        <div style={{ maxHeight: '38vh', overflowY: 'auto', border: '1px solid #e6e6e6', borderRadius: 3 }}>
          {reassignCandidates.length === 0 ? (
            <div style={{ padding: '0.8em', color: '#999', fontSize: '0.9em' }}>
              No cast member matches “{reassignFilter.trim()}”.
            </div>
          ) : (
            <List divided selection>
              {reassignCandidates.map((x) => (
                <List.Item key={x.uuid} active={reassignTo === x.uuid}
                  onClick={() => !reassigning && setReassignTo(x.uuid)}
                  style={{ padding: '0.5em 0.7em' }}>
                  <List.Content floated="right">
                    <Label basic size="mini" title="dialogue lines">
                      <Icon name="comment" />{x.dialogueCount}
                    </Label>
                  </List.Content>
                  <List.Content>
                    {reassignTo === x.uuid && <Icon name="check" color="green" />}
                    {x.name}
                  </List.Content>
                </List.Item>
              ))}
            </List>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button basic disabled={reassigning}
          onClick={() => { setReassign(null); setReassignTo(null); setReassignFilter(''); }}>Cancel</Button>
        <Button primary loading={reassigning} disabled={!reassignTo || reassigning}
          onClick={() => doReassign(reassignTo)}>
          <Icon name="exchange" /> Reassign
        </Button>
      </Modal.Actions>
    </Modal>
  );

  // Inline mode: render the tool directly (the Green Room provides the surrounding heading).
  if (inline) return (<>{body}{confirmDialog}{reassignDialog}</>);

  return (
    <Modal open={open} size="small" closeOnDimmerClick={false}
      style={{ zIndex: 10000 }} dimmer={{ style: { zIndex: 9999 } }}>
      <Modal.Header>
        <Icon name="users" />
        Manage Cast{litGroupName ? ` — ${litGroupName}` : ''}
      </Modal.Header>

      <Modal.Content>{body}</Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
      </Modal.Actions>

      {/* Nested inside this modal on purpose — Semantic stacks dimmers, so the confirmation reads
          as sitting on top of the cast list it is about rather than replacing it. */}
      {confirmDialog}
      {reassignDialog}
    </Modal>
  );
};

export default ManageCastModal;
